import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { TabView, TabPanel } from "primereact/tabview";
import { Timeline } from "primereact/timeline";
import { Dialog } from "primereact/dialog";
import { Sidebar } from "primereact/sidebar";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Chart } from "primereact/chart";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import SupplierService from "../../services/SupplierService";
import ChequeService from "../../services/ChequeService";
import PromissoryNoteService from "../../services/PromissoryNoteService";
import FinancialAccountService from "../../services/FinancialAccountService";
import SettlementService from "../../services/SettlementService";
import settingsService from "../../services/SettingsService";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";
import SupplierStatementReport from "../../components/Supplier/SupplierStatementReport";

const isTryFinancialAccount = (account) => {
  const currencyCode = String(account?.currencyCode ?? account?.CurrencyCode ?? "TRY").trim().toUpperCase();
  return currencyCode === "TRY" || currencyCode === "TL";
};

const HistoryTimeline = ({ timelineEvents }) => {
  if (timelineEvents.length === 0) {
    return (
      <div className="text-center py-12 !text-slate-400 italic text-sm font-medium">
        Henüz işlem bulunmuyor.
      </div>
    );
  }

  return (
    <Timeline
      value={timelineEvents}
      pt={{
        marker: {
          className: 'flex items-center justify-center shadow-none border-none bg-transparent',
          style: { width: '80px' }
        },
        opposite: {
          className: 'hidden'
        }
      }}
      marker={(item) => (
        <div
          className="flex items-center justify-center rounded-2xl shadow-lg border-2 border-white dark:border-slate-800 ring-2 ring-slate-100 dark:ring-slate-950/40 shrink-0"
          style={{
            backgroundColor: item.color,
            width: '45px',
            height: '45px',
            minWidth: '45px',
            minHeight: '45px'
          }}
        >
          <i className={`${item.icon} text-white text-lg`} />
        </div>
      )}
      content={(item) => (
        <div className="mb-3 ml-1 flex-1">
          <div className="font-bold text-slate-800 dark:text-slate-200 text-sm mb-1">{item.status}</div>
          <p className="m-0 text-xs text-slate-500 dark:text-slate-400 mb-1">{item.details}</p>

          <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded-xl border border-slate-100 gap-2 shadow-sm">
            <span className={`text-[11px] font-black px-3 py-1.5 rounded-lg uppercase shrink-0 ${
              item.type === 'payment' ? '!bg-emerald-500 text-white' : '!bg-blue-600 text-white'
            }`}>
              {item.amount.toLocaleString()} ₺
            </span>
            <div className="flex items-center gap-2 text-slate-400">
              <i className="pi pi-clock text-[10px]"></i>
              <small className="font-bold text-[10px]">{item.date}</small>
            </div>
          </div>
        </div>
      )}
    />
  );
};

export default function SupplierDetail() {
  const { id } = useParams();
  const supplierId = Number(id);
  const navigate = useNavigate();
  const [workTrackingParams, setWorkTrackingParams] = useSearchParams();
  const workTrackingActionHandled = useRef(false);
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState(false);
  const [returnHistory, setReturnHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [fullDetail, setFullDetail] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewPreference, setViewPreference] = useState(null);
  const [showPreferenceDialog, setShowPreferenceDialog] = useState(false);
  const [activeLeftTab, setActiveLeftTab] = useState(0);
  
  // Offset / Settlement States
  const [showOffsetDialog, setShowOffsetDialog] = useState(false);
  const [offsetSourceInvoice, setOffsetSourceInvoice] = useState(null);
  const [offsetTargetInvoice, setOffsetTargetInvoice] = useState(null);
  const [offsetAmount, setOffsetAmount] = useState(0);
  const [offsetMode, setOffsetMode] = useState('sales'); // 'sales' or 'purchase'
  const [showGeneralOffsetDialog, setShowGeneralOffsetDialog] = useState(false);
  const [generalOffsetAmount, setGeneralOffsetAmount] = useState(0);
  const [offsetSubmitting, setOffsetSubmitting] = useState(false);
  const [selectedOffsetCustomerInvoices, setSelectedOffsetCustomerInvoices] = useState([]);
  const [selectedOffsetSupplierInvoices, setSelectedOffsetSupplierInvoices] = useState([]);
  
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [statementStartDate, setStatementStartDate] = useState(null);
  const [statementEndDate, setStatementEndDate] = useState(null);
  const [statementShowProducts, setStatementShowProducts] = useState(true);
  const [statementIncludeSales, setStatementIncludeSales] = useState(false);
  const [statementSelectedBanks, setStatementSelectedBanks] = useState([]);
  const supplierStatementReportRef = useRef(null);
  const location = useLocation();
  const toast = useRef(null);
  const paymentAmountRef = useRef(null);

  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [selectedPayableId, setSelectedPayableId] = useState(null);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Nakit");
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentDescription, setPaymentDescription] = useState("");
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [installmentCount, setInstallmentCount] = useState(1);
  const [firstInstallmentDate, setFirstInstallmentDate] = useState(new Date());

  useEffect(() => {
    if (paymentDialogVisible) {
      setInstallmentCount(1);
      setFirstInstallmentDate(new Date());
    }
  }, [paymentDialogVisible]);

  const [detailDialogVisible, setDetailDialogVisible] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const menuRef = useRef(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const mergedInvoices = useMemo(() => {
    const list = [];
    invoiceHistory.forEach(inv => {
      list.push({
        ...inv,
        id: inv.id,
        type: 'purchase',
        invoiceNo: inv.invoiceNo,
        date: inv.date,
        totalQuantity: inv.totalQuantity,
        totalAmount: inv.totalAmount,
        paidAmount: inv.totalAmount - inv.remainingAmount,
        remainingAmount: inv.remainingAmount,
        dueDate: inv.dueDate,
        receivableDueItemId: null,
        supplierPayableDueItemId: inv.supplierPayableDueItemId
      });
    });
    customerInvoices.forEach(inv => {
      list.push({
        ...inv,
        id: inv.id,
        type: 'sales',
        invoiceNo: inv.saleNo || String(inv.id),
        date: inv.saleDate,
        totalQuantity: inv.totalQuantity,
        totalAmount: inv.totalAmount,
        paidAmount: inv.paidAmount,
        remainingAmount: inv.totalAmount - inv.paidAmount,
        dueDate: inv.dueDate,
        receivableDueItemId: inv.receivableDueItemId,
        supplierPayableDueItemId: null
      });
    });

    const parseDate = (dStr) => {
      if (!dStr) return new Date(0);
      const parts = dStr.split('.');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return new Date(dStr);
    };
    return list.sort((a, b) => parseDate(b.date) - parseDate(a.date));
  }, [invoiceHistory, customerInvoices]);

  const filteredInvoiceHistory = useMemo(() => {
    if (!showOnlyUnpaid) return mergedInvoices;
    return mergedInvoices.filter(inv => inv.remainingAmount > 0.01);
  }, [mergedInvoices, showOnlyUnpaid]);

  const invoiceMenuItems = useMemo(() => {
    if (!selectedInvoice) return [];

    const items = [
      {
        label: 'Detay',
        icon: 'pi pi-search',
        command: () => handleShowDetail(selectedInvoice.id)
      },
      {
        label: 'Düzenle',
        icon: 'pi pi-pencil',
        command: () => navigate(`/suppliers/purchase/edit/${selectedInvoice.id}`)
      }
    ];

    if (selectedInvoice.isDueTrackingActive && selectedInvoice.remainingAmount > 0) {
      items.push({
        label: 'Ödeme Yap',
        icon: 'pi pi-wallet',
        command: () => {
          setPaymentMethod("Nakit");
          setUseExistingDocument(false);
          setSelectedDocument(null);
          setPaymentAmount(selectedInvoice.remainingAmount);
          setPaymentDescription(`${selectedInvoice.invoiceNo} nolu fatura ödemesi`);
          setSelectedPayableId(selectedInvoice.supplierPayableDueItemId || null);
          setPaymentDialogVisible(true);
        }
      });
    }

    return items;
  }, [selectedInvoice, navigate]);

  // Return Product State
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [mounted, setMounted] = useState(false);
  const [selectedReturnRow, setSelectedReturnRow] = useState(null);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const tooltipRef = useRef(null);
  
  // Dynamic Sidebar Resizing
  const [sidebarWidth, setSidebarWidth] = useState(720);
  const isResizing = useRef(false);

  const handleResizing = useCallback((e) => {
    if (!isResizing.current) return;
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= 450 && newWidth <= window.innerWidth * 0.95) {
      setSidebarWidth(newWidth);
    }
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "default";
    document.body.style.userSelect = "auto";
  }, [handleResizing]);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [handleResizing, stopResizing]);

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleResizing);
      document.removeEventListener("mouseup", stopResizing);
    };
  }, [handleResizing, stopResizing]);

  const paymentMethods = [
    { label: "Nakit", value: "Nakit", icon: "pi-money-bill", desc: "Kasadan anlık nakit çıkışı", category: "Likit" },
    { label: "Banka Transferi", value: "Banka Transferi", icon: "pi-send", desc: "Hesaptan Havale/EFT gönderimi", category: "Likit" },
    { label: "Kredi Kartı", value: "Kredi Kartı", icon: "pi-credit-card", desc: "Şirket kredi kartı ile ödeme", category: "Dijital" },
    { label: "Sanal POS", value: "Sanal POS", icon: "pi-globe", desc: "Sanal POS ile ödeme", category: "Dijital" },
    { label: "Çek (Kendi Çekimiz)", value: "Çek", icon: "pi-id-card", desc: "Şirket çeki ile vadeli ödeme", category: "Evrak" },
    { label: "Senet (Kendi Senedimiz)", value: "Senet", icon: "pi-file-edit", desc: "Kendi senedimiz ile borçlanma", category: "Evrak" },
    { label: "Çek Cirosu", value: "ChequeEndorse", icon: "pi-sync", desc: "Müşteriden alınan çeki devret", category: "Evrak" },
    { label: "Senet Cirosu", value: "PromissoryEndorse", icon: "pi-sync", desc: "Müşteriden alınan senedi devret", category: "Evrak" }
  ];

  const [portfolioCheques, setPortfolioCheques] = useState([]);
  const [paymentFilters, setPaymentFilters] = useState({
    global: { value: null, matchMode: "contains" },
    date: { value: null, matchMode: "contains" },
    description: { value: null, matchMode: "contains" },
    paymentMethod: { value: null, matchMode: "equals" }
  });
  const [portfolioNotes, setPortfolioNotes] = useState([]);
  const [ownPortfolioCheques, setOwnPortfolioCheques] = useState([]);
  const [ownPortfolioNotes, setOwnPortfolioNotes] = useState([]);
  const [useExistingDocument, setUseExistingDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const filteredAccounts = useMemo(() => {
    const tryAccounts = financialAccounts.filter(isTryFinancialAccount);
    if (paymentMethod === "Nakit") return tryAccounts.filter(a => a.accountType === 1);
    if (paymentMethod === "Banka Transferi" || paymentMethod === "ChequeEndorse" || paymentMethod === "PromissoryEndorse" || paymentMethod === "Havale" || paymentMethod === "EFT") return tryAccounts.filter(a => a.accountType === 2);
    if (paymentMethod === "Kredi Kartı") return tryAccounts.filter(a => a.accountType === 3);
    if (paymentMethod === "Sanal POS" || paymentMethod === "SanalPos") return tryAccounts.filter(a => a.accountType === 5 && a.supplierId === parseInt(id));
    return [];
  }, [financialAccounts, paymentMethod, id]);

  const bankAccountOptions = useMemo(() => {
    return financialAccounts
      .filter(isTryFinancialAccount)
      .filter((a) => a.accountType === 2 || a.accountType === 1)
      .map((a) => ({
        label: `${a.name} (${a.accountType === 2 ? "Banka" : "Kasa"}) - Bakiye: ₺${(Number(a.balance) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
        value: a.id,
        name: a.name,
      }));
  }, [financialAccounts]);

  useEffect(() => {
    if (filteredAccounts.length === 1) {
      setSelectedAccountId(filteredAccounts[0].id);
      if (showEditPaymentDialog && editingPayment) {
        setEditingPayment(prev => ({ ...prev, financialAccountId: filteredAccounts[0].id }));
      }
    } else {
      setSelectedAccountId(null);
    }
  }, [filteredAccounts, showEditPaymentDialog, editingPayment]);

  const [chequeDetails, setChequeDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: ""
  });

  const [promissoryDetails, setPromissoryDetails] = useState({
    dueDate: null,
    drawer: "",
    guarantor: ""
  });

const formatDateOnly = (date) => {
  if (!date) return null;

  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
  useEffect(() => {
    if (paymentMethod === "ChequeEndorse") {
      // status: 1 (Portfolio), type: 1 (Inbound - Customer document)
      ChequeService.getCheques(1, 1).then(res => setPortfolioCheques(res.data || []));
    } else if (paymentMethod === "PromissoryEndorse") {
      // status: 1 (Portfolio), type: 1 (Inbound - Customer document)
      PromissoryNoteService.getPromissoryNotes(1, 1).then(res => setPortfolioNotes(res.data || []));
    } else if (paymentMethod === "Çek") {
      // status: 1 (Portfolio), type: 2 (Outbound - Our document)
      ChequeService.getCheques(1, 2).then(res => setOwnPortfolioCheques(res.data || []));
    } else if (paymentMethod === "Senet") {
      // status: 1 (Portfolio), type: 2 (Outbound - Our document)
      PromissoryNoteService.getPromissoryNotes(1, 2).then(res => setOwnPortfolioNotes(res.data || []));
    }
  }, [paymentMethod]);
 useEffect(() => {
  if (!toast.current) return;

  if (location.state?.from === "purchase-create") {
    toast.current.show({
      severity: "success",
      summary: "Alış kaydı eklendi",
      detail: "Stok ve cari güncellendi",
      life: 2500,
    });
  }
}, [location.state]);
  /* =====================
     FETCH DATA
  ===================== */
  const fetchSupplierDetail = useCallback(async () => {
    if (!supplierId) return;
    try {

      const data = await SupplierService.getSupplierDetail(supplierId);
      setFullDetail(data);

      setSupplier(data.supplier);
      setPurchaseHistory(data.purchaseHistory || []);
      setInvoiceHistory(data.invoiceHistory || []);
      setReturnHistory(data.returnHistory || []);
      setPaymentHistory(data.payments || []);
      setUpcomingPayments(data.upcomingPayments || []);
      setTimelineEvents(data.transactions || []);
      setCustomerInvoices(data.customerInvoices || []);
      setSummaryData(data.summary);
    } catch (error) {
      console.error("Supplier detail error:", error);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  const fetchTenantSettings = useCallback(async () => {
    try {
      const res = await settingsService.getTenantSettings();
      if (res.data && res.data.success) {
        const settings = res.data.data;
        const pref = settings["SupplierDetailViewPreference"];
        if (pref) {
          setViewPreference(pref);
          if (pref === "INVOICE") {
            setActiveLeftTab(1);
          } else {
            setActiveLeftTab(0);
          }
        } else {
          setShowPreferenceDialog(true);
        }
      }
    } catch (err) {
      console.error("Görünüm ayarları alınırken hata:", err);
    }
  }, []);

  const handleSavePreference = async (choice) => {
    try {
      await settingsService.saveTenantSetting({
        settingKey: "SupplierDetailViewPreference",
        settingValue: choice
      });
      setViewPreference(choice);
      if (choice === "INVOICE") {
        setActiveLeftTab(1);
      } else {
        setActiveLeftTab(0);
      }
      setShowPreferenceDialog(false);
      toast.current.show({ severity: "success", summary: "Başarılı", detail: "Görünüm tercihi kaydedildi." });
      await fetchSupplierDetail();
    } catch (err) {
      console.error("Görünüm tercihi kaydedilirken hata:", err);
      toast.current.show({ severity: "error", summary: "Hata", detail: "Görünüm tercihi kaydedilemedi." });
    }
  };

  const handleAutoOffset = async (row, mode) => {
    if (!supplier.relatedCustomerId) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "İlişkili müşteri hesabı bulunmadığı için mahsuplaşma yapılamaz.",
      });
      return;
    }

    let amount = 0;
    let customerDueItemId = null;
    let supplierDueItemId = null;
    let desc = "";

    if (mode === 'sales') {
      const openSalesAmount = row.totalAmount - row.paidAmount;
      const totalOpenSupplierAmount = invoiceHistory.filter(x => x.isDueTrackingActive).reduce((sum, inv) => sum + inv.remainingAmount, 0);
      amount = Math.min(openSalesAmount, totalOpenSupplierAmount);
      customerDueItemId = row.receivableDueItemId;
      desc = `${row.saleNo} nolu satış faturası için otomatik mahsuplaşma (FIFO)`;
    } else {
      const openPurchaseAmount = row.remainingAmount;
      const totalOpenCustomerAmount = customerInvoices.reduce((sum, inv) => sum + (inv.totalAmount - inv.paidAmount), 0);
      amount = Math.min(openPurchaseAmount, totalOpenCustomerAmount);
      supplierDueItemId = row.supplierPayableDueItemId;
      desc = `${row.invoiceNo} nolu alış faturası için otomatik mahsuplaşma (FIFO)`;
    }

    if (amount <= 0.01) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Mahsup edilebilecek açık bakiye bulunamadı.",
      });
      return;
    }

    try {
      setOffsetSubmitting(true);
      const payload = {
        customerId: supplier.relatedCustomerId,
        supplierId: supplier.id,
        amount: amount,
        date: new Date().toISOString(),
        description: desc,
        customerDueItemId: customerDueItemId,
        supplierDueItemId: supplierDueItemId
      };
      const res = await SettlementService.createSettlement(payload);
      if (res.success !== false) {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Otomatik mahsuplaşma başarıyla tamamlandı.",
        });
        fetchSupplierDetail();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "İşlem başarısız.",
        });
      }
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Mahsup işlemi sırasında bir hata oluştu.",
      });
    } finally {
      setOffsetSubmitting(false);
    }
  };

  const handleOffsetSubmit = async () => {
    if (!offsetTargetInvoice || offsetAmount <= 0) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Lütfen hedef fatura ve geçerli bir tutar girin.",
      });
      return;
    }

    try {
      setOffsetSubmitting(true);
      const payload = {
        customerId: supplier.relatedCustomerId,
        supplierId: supplier.id,
        amount: offsetAmount,
        date: new Date().toISOString(),
        description: offsetMode === 'sales'
          ? `${offsetSourceInvoice.saleNo} nolu satış faturası ile ${offsetTargetInvoice.invoiceNo} nolu alış faturası mahsup edildi.`
          : `${offsetTargetInvoice.saleNo} nolu satış faturası ile ${offsetSourceInvoice.invoiceNo} nolu alış faturası mahsup edildi.`,
        customerDueItemId: offsetMode === 'sales' ? offsetSourceInvoice.receivableDueItemId : offsetTargetInvoice.receivableDueItemId,
        supplierDueItemId: offsetMode === 'sales' ? offsetTargetInvoice.supplierPayableDueItemId : offsetSourceInvoice.supplierPayableDueItemId
      };

      const res = await SettlementService.createSettlement(payload);
      if (res.success !== false) {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Mahsup işlemi başarıyla tamamlandı.",
        });
        setShowOffsetDialog(false);
        fetchSupplierDetail();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "İşlem başarısız.",
        });
      }
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Mahsup işlemi sırasında bir hata oluştu.",
      });
    } finally {
      setOffsetSubmitting(false);
    }
  };

  const calculateOffsets = useCallback((selectedCustInvoices, selectedSuppInvoices) => {
    const custItems = selectedCustInvoices.map(inv => ({
      ...inv,
      remaining: inv.totalAmount - inv.paidAmount
    }));
    const suppItems = selectedSuppInvoices.map(inv => ({
      ...inv,
      remaining: inv.remainingAmount
    }));

    const steps = [];
    let cIdx = 0;
    let sIdx = 0;

    while (cIdx < custItems.length && sIdx < suppItems.length) {
      const cust = custItems[cIdx];
      const supp = suppItems[sIdx];

      if (cust.remaining <= 0.009) { cIdx++; continue; }
      if (supp.remaining <= 0.009) { sIdx++; continue; }

      const amount = Math.min(cust.remaining, supp.remaining);
      if (amount > 0) {
        steps.push({
          customerInvoice: cust,
          supplierInvoice: supp,
          amount: amount,
          customerDueItemId: cust.receivableDueItemId,
          supplierDueItemId: supp.supplierPayableDueItemId
        });
        cust.remaining -= amount;
        supp.remaining -= amount;
      }
    }
    return steps;
  }, []);



  const offsetSteps = useMemo(() => {
    return calculateOffsets(selectedOffsetCustomerInvoices, selectedOffsetSupplierInvoices);
  }, [selectedOffsetCustomerInvoices, selectedOffsetSupplierInvoices, calculateOffsets]);

  const totalOffsetAmount = useMemo(() => {
    return offsetSteps.reduce((sum, step) => sum + step.amount, 0);
  }, [offsetSteps]);

  const handleGeneralOffsetSubmit = async () => {
    if (offsetSteps.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Eşleştirilebilir tutar bulunamadı. Lütfen faturaları kontrol edin.",
      });
      return;
    }

    try {
      setOffsetSubmitting(true);
      
      // Perform matches sequentially
      for (const step of offsetSteps) {
        const payload = {
          customerId: supplier.relatedCustomerId,
          supplierId: supplier.id,
          amount: step.amount,
          date: new Date().toISOString(),
          description: `${step.customerInvoice.saleNo} nolu satış faturası ile ${step.supplierInvoice.invoiceNo} nolu alış faturası mahsup edildi.`,
          customerDueItemId: step.customerDueItemId,
          supplierDueItemId: step.supplierDueItemId
        };

        const res = await SettlementService.createSettlement(payload);
        if (res.success === false) {
          throw new Error(res.message || "Mahsup adımlarından birinde hata oluştu.");
        }
      }

      toast.current.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Seçilen faturaların mahsuplaşması başarıyla tamamlandı.",
      });
      setShowGeneralOffsetDialog(false);
      fetchSupplierDetail();
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: err.message || "Mahsup işlemi sırasında bir hata oluştu.",
      });
    } finally {
      setOffsetSubmitting(false);
    }
  };

  useEffect(() => {
    setMounted(true);
    fetchSupplierDetail();
    fetchTenantSettings();
    FinancialAccountService.getAll().then(res => {
      if (res.success) {
        setFinancialAccounts(res.data || []);
      }
    });
  }, [fetchSupplierDetail, fetchTenantSettings]);

  useEffect(() => {
    if (loading || workTrackingActionHandled.current || workTrackingParams.get("action") !== "pay") {
      return;
    }

    const payableId = Number(workTrackingParams.get("payableId"));
    if (!Number.isFinite(payableId) || payableId <= 0) {
      return;
    }

    const invoice = invoiceHistory.find((item) => Number(item.supplierPayableDueItemId) === payableId);
    const upcoming = upcomingPayments.find((item) => Number(item.supplierPayableDueItemId ?? item.id) === payableId);
    const remainingAmount = Number(invoice?.remainingAmount ?? upcoming?.remainingAmount ?? upcoming?.amount ?? 0);

    workTrackingActionHandled.current = true;
    setSelectedPayableId(payableId);
    setPaymentAmount(remainingAmount > 0 ? remainingAmount : 0);
    setPaymentDescription(
      invoice
        ? `${invoice.invoiceNo || invoice.id} nolu fatura ödemesi`
        : upcoming?.description || "İş Takibi üzerinden tedarikçi ödemesi"
    );
    setActiveLeftTab(1);
    setPaymentDialogVisible(true);

    const nextParams = new URLSearchParams(workTrackingParams);
    nextParams.delete("action");
    nextParams.delete("payableId");
    setWorkTrackingParams(nextParams, { replace: true });
  }, [invoiceHistory, loading, setWorkTrackingParams, upcomingPayments, workTrackingParams]);
 
  /* =====================
     PAYMENT LOGIC
  ===================== */
  const handlePayment = async () => {
    if (paymentAmount <= 0 && paymentMethod !== "ChequeEndorse" && paymentMethod !== "PromissoryEndorse" && !useExistingDocument) return;

    try {
      setSubmittingPayment(true);

      let res;
      if (paymentMethod === "ChequeEndorse" || (paymentMethod === "Çek" && useExistingDocument)) {
        if (!selectedDocument) return;
        res = await ChequeService.endorseCheque({
          chequeId: selectedDocument.id,
          supplierId: supplierId,
          endorsementDate: formatDateOnly(paymentDate),
          description: paymentDescription,
          financialAccountId: selectedAccountId,
          supplierPayableDueItemId: selectedPayableId
        });
      } else if (paymentMethod === "PromissoryEndorse" || (paymentMethod === "Senet" && useExistingDocument)) {
        if (!selectedDocument) return;
        res = await PromissoryNoteService.endorsePromissoryNote({
          promissoryNoteId: selectedDocument.id,
          supplierId: supplierId,
          endorsementDate: formatDateOnly(paymentDate),
          description: paymentDescription,
          financialAccountId: selectedAccountId,
          supplierPayableDueItemId: selectedPayableId
        });
      } else {
        if(paymentMethod === "Çek") {
        chequeDetails.dueDate = formatDateOnly(chequeDetails.dueDate);
        }
        if(paymentMethod === "Senet") {
          promissoryDetails.dueDate = formatDateOnly(promissoryDetails.dueDate);
        } 
        const payload = {
          supplierId: supplierId,
          amount: paymentAmount,
          paymentMethod: paymentMethod,
          financialAccountId: selectedAccountId,
          paymentDate: formatDateOnly(paymentDate),
          description: paymentDescription,
          installmentCount: installmentCount,
          firstInstallmentDate: installmentCount > 1 ? formatDateOnly(firstInstallmentDate) : null,
          supplierPayableDueItemId: selectedPayableId,
          ...(paymentMethod === "Çek" ? chequeDetails : {}),
          ...(paymentMethod === "Senet" ? promissoryDetails : {})
        };
        res = await SupplierService.receivePayment(payload);
      }
      if (res.success) {
        setPaymentDialogVisible(false);
        setSelectedPayableId(null);
        setPaymentAmount(0);
        setPaymentDescription("");
        await fetchSupplierDetail();
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Ödeme başarıyla kaydedildi.",
          life: 3000,
        });
      }
      else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: "Ödeme kaydedilemedi.",
          life: 3000,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || editingPayment.amount <= 0) return;

    try {
      setSubmittingPayment(true);

      const toIsoString = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return d.toISOString();
      };

      const res = await SupplierService.updatePayment({
        transactionId: Number(editingPayment.id),
        amount: Number(editingPayment.amount),
        paymentMethod: String(editingPayment.paymentMethod || "Nakit"),
        financialAccountId: editingPayment.financialAccountId ? Number(editingPayment.financialAccountId) : null,
        paymentDate: toIsoString(editingPayment.paymentDate),
        description: String(editingPayment.description || ""),
        bankName: editingPayment.bankName ? String(editingPayment.bankName) : null,
        bankBranch: editingPayment.bankBranch ? String(editingPayment.bankBranch) : null,
        checkNumber: editingPayment.checkNumber ? String(editingPayment.checkNumber) : null,
        dueDate: editingPayment.dueDate ? toIsoString(editingPayment.dueDate) : null,
        drawer: editingPayment.drawer ? String(editingPayment.drawer) : null,
        guarantor: editingPayment.guarantor ? String(editingPayment.guarantor) : null
      });

      if (res.success) {
        setShowEditPaymentDialog(false);
        setEditingPayment(null);
        await fetchSupplierDetail();
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Ödeme başarıyla güncellendi.",
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "Ödeme güncellenemedi.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.current.show({
        severity: "error",
        summary: "Sistem Hatası",
        detail: error.response?.data?.message || error.message || "Ödeme güncellenirken bir hata oluştu.",
        life: 5000,
      });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleDeleteReturn = (record) => {
    confirmDialog({
        message: 'Bu iade işlemini iptal etmek istediğinize emin misiniz? Stoklar ve tedarikçi bakiyesi geri alınacaktır.',
        header: 'İade İptal Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, İptal Et',
        rejectLabel: 'Vazgeç',
        accept: async () => {
            try {
                const res = await SupplierService.deleteReturn(record.id);
                if (res.success) {
                    toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İade iptal edildi', life: 3000 });
                    await fetchSupplierDetail();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message || 'İade iptal edilemedi', life: 3000 });
                }
            } catch (err) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İade iptal edilirken hata oluştu', life: 3000 });
            }
        }
    });
  };

  const handleDeletePayment = (record) => {
    const isDocument = record.referenceType === "CHEQUE" || record.referenceType === "PROMISSORY_NOTE";
    confirmDialog({
        message: isDocument ? "Çekin/Senetin verildiği tedarikçi silinecektir onaylıyor musunuz?" : 'Bu ödeme işlemini silmek istediğinize emin misiniz?',
        header: 'Silme Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, Sil',
        rejectLabel: 'Vazgeç',
        accept: async () => {
            try {
                const res = await SupplierService.deletePayment(record.id);
                if (res.success) {
                    toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İşlem silindi', life: 3000 });
                    await fetchSupplierDetail();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message || 'İşlem silinemedi', life: 3000 });
                }
            } catch (err) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem silinirken hata oluştu', life: 3000 });
            }
        }
    });
  };

  const handleShowDetail = async (id) => {
    try {
      setLoadingDetail(true);
      setDetailDialogVisible(true);
      const res = await SupplierService.getPurchaseTransactionDetail(id);
      if (res.success) {
        setInvoiceDetail(res.data);
      }
    } catch (error) {
      console.error("Error fetching invoice detail:", error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReturnProduct = (rowData) => {
    // Check if the whole invoice will become empty
    const rowProductId = rowData.productId || rowData.ProductId;
    const rowTransactionId = rowData.transactionId || rowData.TransactionId;

    const sameInvoiceProducts = purchaseHistory.filter(p => (p.transactionId || p.TransactionId) === rowTransactionId);
    const totalQtyInInvoice = sameInvoiceProducts.reduce((sum, p) => sum + p.quantity, 0);
    const willBeEmpty = (totalQtyInInvoice - returnQuantity) === 0;

    const proceedReturn = async () => {
      try {
        setSubmittingReturn(true);
        const res = await SupplierService.createPurchaseReturnFromLine({
          purchaseTransactionId: rowTransactionId,
          productId: rowProductId,
          quantity: returnQuantity,
          description: "Alıştan İade"
        });
        if (res.data?.success || res.success) {
          setShowReturnDialog(false);
          toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İade işlemi kaydedildi', life: 3000 });
          await fetchSupplierDetail();
        } else {
          toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message || 'İade alınırken hata oluştu', life: 3000 });
        }
      } catch (err) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İade alınırken hata oluştu', life: 3000 });
      } finally {
        setSubmittingReturn(false);
      }
    };

    if (willBeEmpty) {
      confirmDialog({
        message: 'Faturada ürün kalmayacak onaylıyor musunuz?',
        header: 'Kritik İade Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, Faturayı İptal Et ve İade Al',
        rejectLabel: 'Vazgeç',
        accept: proceedReturn
      });
    } else {
      confirmDialog({
        message: `${rowData.productName} ürününden ${returnQuantity} adet iade etmek istediğinize emin misiniz?`,
        header: 'İade Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, İade Et',
        rejectLabel: 'Vazgeç',
        accept: proceedReturn
      });
    }
  };

  /* =====================
     GUARDS
  ===================== */
  if (loading || !supplier || (viewPreference === null && !showPreferenceDialog)) {
    return (
      <div className="p-4">
        <Skeleton height="4rem" className="mb-4 rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-4"><Skeleton height="20rem" className="rounded-xl" /></div>
          <div className="md:col-span-8"><Skeleton height="20rem" className="rounded-xl" /></div>
        </div>
      </div>
    );
  }

  // Chart Data (Mocking for now as we don't have historical monthly data yet)
  const chartData = {
    labels: ["Oca", "Şub", "Mar", "Nis", "May", "Haz"],
    datasets: [
      {
        label: "Alış Tutarı (₺)",
        data: [0, 0, 0, 0, 0, summaryData?.totalPurchaseAmount || 0],
        borderColor: "#42A5F5",
        tension: 0.4,
        fill: false,
      },
    ],
  };

  

  const chartOptions = {
    plugins: { legend: { display: false } },
    maintainAspectRatio: false,
  };

  return (
    <div className="p-2 space-y-2 animate-fade-in">
<style>{`
  /* Dark mode overrides for Supplier Detail Page */
  .dark .bg-white {
    background-color: #1e293b !important;
    border-color: #334155 !important;
  }

  .dark .border-slate-200,
  .dark .border-slate-100 {
    border-color: #334155 !important;
  }

  .dark .border-amber-500 {
    border-color: #f59e0b !important;
  }
  .dark .border-emerald-500 {
    border-color: #10b981 !important;
  }
  .dark .border-blue-500 {
    border-color: #3b82f6 !important;
  }

  .dark .bg-slate-50,
  .dark .bg-slate-100,
  .dark .bg-slate-50\\/50,
  .dark .bg-slate-50\\/80 {
    background-color: #0f172a !important;
    border-color: #334155 !important;
  }

  .dark .p-tabview .p-tabview-nav {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
  
  .dark .p-tabview .p-tabview-nav li .p-tabview-nav-link {
    background: #1e293b !important;
    color: #94a3b8 !important;
    border-color: #334155 !important;
  }

  .dark .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
    background: #1e293b !important;
    color: #6366f1 !important;
    border-color: #6366f1 !important;
  }

  .dark .p-tabview .p-tabview-panels {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .dark .p-dialog .p-dialog-header,
  .dark .p-dialog .p-dialog-content,
  .dark .p-dialog .p-dialog-footer {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-title {
    color: #ffffff !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-header-icon {
    color: #cbd5e1 !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-header-icon:hover {
    background: #334155 !important;
    color: #ffffff !important;
  }
  
  .dark .p-datatable .p-paginator {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #cbd5e1 !important;
  }

  .dark .p-sidebar {
    background: #1e293b !important;
    color: #cbd5e1 !important;
  }

  .dark .supplier-invoice-table .unpaid-invoice-row td {
    background-color: #450a0a !important;
    color: #fecaca !important;
  }

  .dark .supplier-invoice-table .unpaid-invoice-row:hover td {
    background-color: #7f1d1d !important;
  }

  .dark .\\!bg-red-50\\/80 {
    background-color: rgba(69, 10, 10, 0.4) !important;
  }
`}</style>
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
      <Menu model={invoiceMenuItems} popup ref={menuRef} id="invoice_menu" appendTo={mounted ? document.body : "self"} />
      <ConfirmDialog />
      <Dialog
        header="Görüntüleme Tercihi Belirleyin"
        visible={showPreferenceDialog}
        style={{ width: '450px' }}
        modal
        closable={false}
        className="p-fluid rounded-3xl overflow-hidden"
        appendTo={mounted ? document.body : "self"}
      >
        <div className="text-center p-4">
          <i className="pi pi-sliders-h text-5xl text-blue-600 mb-4 animate-bounce"></i>
          <h3 className="text-lg font-black text-slate-800 mb-2">Tedarikçi Detay Ekranı Tercihi</h3>
          <p className="text-xs text-slate-500 leading-relaxed mb-6">
            Tedarikçi detay ekranında öncelikli olarak hangi bilgileri görmek istersiniz? Bu tercihiniz sistem performansını ve sayfa açılış hızını optimize edecektir.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              label="Fatura Odaklı (Önerilen - Hızlı)"
              icon="pi pi-file"
              className="p-button-raised p-button-primary py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all"
              onClick={() => handleSavePreference("INVOICE")}
            />
            <Button
              label="Ürün Odaklı (Tüm Ürün Detayları)"
              icon="pi pi-box"
              className="p-button-outlined p-button-secondary py-3 rounded-xl font-bold hover:bg-slate-50 transition-all"
              onClick={() => handleSavePreference("PRODUCT")}
            />
          </div>
        </div>
      </Dialog>
      <Tooltip
        ref={tooltipRef}
        appendTo={document.body}
        position="top"
        baseZIndex={99999}
      />
      {/* Ürün İade Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <i className="pi pi-refresh text-orange-600 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Ürün İade Et</h3>
              <p className="text-xs text-slate-500 font-medium m-0">İade edilecek miktar bilgisini girin</p>
            </div>
          </div>
        }
        visible={showReturnDialog}
        style={{ width: '400px' }}
        onHide={() => setShowReturnDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowReturnDialog(false)} />
            <Button label="İadeyi Onayla" icon="pi pi-check" className="!bg-orange-600 border-none font-black" onClick={() => handleReturnProduct(selectedReturnRow)} disabled={!returnQuantity || returnQuantity <= 0 || submittingReturn} />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          {selectedReturnRow && (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 mb-2">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">ÜRÜN</div>
              <div className="text-sm font-bold text-slate-700 dark:text-slate-300">{selectedReturnRow.productName}</div>
              <div className="mt-2 flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500">Maksimum İade Edilebilir:</span>
                <span className="text-xs font-black text-slate-900">{selectedReturnRow.quantity} Adet</span>
              </div>
            </div>
          )}
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İade Adedi</label>
            <InputNumber
              value={returnQuantity}
              onValueChange={(e) => setReturnQuantity(e.value)}
              min={1}
              max={selectedReturnRow?.quantity}
              placeholder="Miktar giriniz..."
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </Dialog>
      <div className="bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden no-print">
        {!isExpanded ? (
          <div className="customer-info-compact-enter p-2 md:p-3">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">

              {/* Sol alan */}
              <div className="flex items-start sm:items-center gap-3 md:gap-4 min-w-0">
                <Button
                  icon="pi pi-arrow-left"
                  className="p-button-text p-button-secondary !rounded-2xl !h-11 !w-11 bg-white border border-slate-200 shadow-sm hover:bg-slate-50 shrink-0"
                  onClick={() => navigate("/suppliers")}
                  tooltip="Tedarikçi listesine dön"
                />

                <div className="relative shrink-0">
                  <div className="w-13 h-13 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <i className="pi pi-building text-white text-xl"></i>
                  </div>

                  <span
                    className={`absolute -right-1 -bottom-1 w-4 h-4 rounded-full border-2 border-white ${supplier.isActive ? "bg-emerald-500" : "bg-red-500"
                      }`}
                  ></span>
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-black text-slate-400">
                      #{supplier.id}
                    </span>
                    <h1 className="text-lg md:text-xl font-black text-slate-900 m-0 leading-tight truncate max-w-[260px] md:max-w-[420px]">
                      {supplier.name}
                    </h1>

                    <Tag
                      value={supplier.isActive ? "Aktif" : "Pasif"}
                      severity={supplier.isActive ? "success" : "danger"}
                      className="text-[9px] font-black uppercase px-2.5 py-0.5 rounded-lg"
                    />
                  </div>

                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                      <i className="pi pi-phone text-slate-400"></i>
                      {supplier.phoneNumber || "Telefon yok"}
                    </span>

                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 max-w-[220px] truncate">
                      <i className="pi pi-envelope text-slate-400"></i>
                      {supplier.email || "E-posta yok"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Orta özet alanı */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 xl:min-w-[520px]">
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 px-4 py-3">
                  <div className="text-[11px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                    Toplam Alış
                  </div>
                  <div className="mt-1 text-sm font-black text-slate-800 dark:text-slate-200">
                    ₺{summaryData?.totalPurchaseAmount?.toLocaleString("tr-TR", { minimumFractionDigits: 2 }) || '0,00'}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 px-4 py-3">
                  <div className="text-[11px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                    Alış Sayısı
                  </div>
                  <div className="mt-1 text-sm font-black text-blue-600 dark:text-blue-400">
                    {summaryData?.totalPurchaseCount || 0}
                  </div>
                </div>

                <div
                  className={`rounded-2xl px-4 py-3 border ${supplier.balance < 0
                    ? "!bg-red-50 dark:!bg-red-950/20 border-red-100 dark:border-red-900/30"
                    : "!bg-emerald-50 dark:!bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/30"
                    }`}
                >
                  <div
                    className={`text-[11px] font-black uppercase tracking-widest ${supplier.balance < 0
                      ? "!text-red-400"
                      : "!text-emerald-400"
                      }`}
                  >
                    Güncel Bakiye
                  </div>

                  <div
                    className={`mt-1 text-sm font-black ${supplier.balance < 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-emerald-700 dark:text-emerald-400"
                      }`}
                  >
                    ₺{Math.abs(supplier.balance).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Sağ aksiyon alanı */}
              <div className="flex items-center justify-between xl:justify-end gap-2 flex-wrap">
                <Button
                  label="Ödeme Yap"
                  icon="pi pi-wallet"
                  className="p-button-sm !rounded-xl font-bold !bg-emerald-600 !border-emerald-600 shadow-md shadow-emerald-500/20"
                  onClick={() => {
                    setPaymentMethod("Nakit");
                    setUseExistingDocument(false);
                    setSelectedDocument(null);
                    setPaymentAmount(0);
                    setSelectedPayableId(null);
                    setPaymentDialogVisible(true);
                  }}
                />

                <Button
                  label="Ürün Al"
                  icon="pi pi-shopping-cart"
                  className="p-button-sm !rounded-xl font-bold !bg-blue-600 !border-blue-600 shadow-md shadow-blue-500/20"
                  onClick={() => navigate("/suppliers/purchase", { state: { supplierId, supplierName: supplier.name } })}
                />

                <Button
                  label="Satış Yap"
                  icon="pi pi-download"
                  className="p-button-sm !rounded-xl font-bold !bg-indigo-600 !border-indigo-600 shadow-md shadow-indigo-500/20"
                  onClick={() => {
                    navigate("/sales/productsale", {
                      state: {
                        preSelectedSupplier: {
                          id: supplierId,
                          name: supplier.name,
                          phone: supplier.phoneNumber,
                        },
                      },
                    });
                  }}
                />

                <Button
                  type="button"
                  onClick={() => setIsExpanded(true)}
                  className=" 
    group
    !h-10 !px-3
    !rounded-2xl
    !bg-white
    !border !border-slate-400
    !text-slate-700
    shadow-sm
    hover:!bg-slate-900
    hover:!border-slate-900
    hover:!text-white
    transition-all
    duration-400
  "
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] font-black uppercase tracking-wide">
                      Detay
                    </span>

                    <span
                      className="
        w-8 h-6 rounded-xl
        bg-slate-100
        flex items-center justify-center
        group-hover:bg-white/15
        transition-all duration-400
      "
                    >
                      <i className="pi pi-chevron-down text-[11px] group-hover:translate-y-0.5 transition-transform duration-200"></i>
                    </span>
                  </span>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="customer-info-expand-enter p-4 md:p-5 relative">
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="
    absolute top-4 right-4 z-10
    group
    h-10 px-3
    rounded-2xl
    bg-white/95
    border border-slate-200
    text-slate-700
    shadow-sm
    hover:bg-slate-900
    hover:border-slate-900
    hover:text-white
    transition-all
    duration-200
  "
            >
              <span className="flex items-center gap-2">
                <span className="text-[11px] font-black uppercase tracking-wide">
                  Daralt
                </span>

                <span
                  className="
        w-6 h-6 rounded-xl
        bg-slate-100
        flex items-center justify-center
        group-hover:bg-white/15
        transition-all duration-200
      "
                >
                  <i className="pi pi-chevron-up text-[11px] group-hover:-translate-y-0.5 transition-transform duration-200"></i>
                </span>
              </span>
            </button>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-stretch">

        {/* LEFT - SUPPLIER INFO CARD */}
        <div className="lg:col-span-3">
          <div className="h-full bg-white shadow-sm border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
            <div className="bg-slate-900 p-6 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="rounded-xl flex items-center justify-center w-14 h-14 bg-blue-500/20 border border-blue-500/30">
                  <i className="pi pi-building text-2xl text-blue-400"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="m-0 text-xl font-bold truncate">{supplier.name}</h2>
                  <div className="text-slate-400 text-xs font-semibold mt-1">ID: #{supplier.id}</div>
                </div>
              </div>
              <div className={`inline-flex px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
                supplier.balance < 0 ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              }`}>
                {supplier.balance < 0 ? "Borçlu" : "Alacaklı"}
              </div>
            </div>

            <div className="p-6 space-y-6 flex-1">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-phone text-blue-500"></i> Telefon
                  </span>
                  <div className="text-slate-700 dark:text-slate-200 font-bold">{supplier.phoneNumber || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-envelope text-blue-500"></i> Email
                  </span>
                  <div className="text-slate-700 dark:text-slate-200 font-bold truncate">{supplier.email || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-id-card text-blue-500"></i> Vergi No
                  </span>
                  <div className="text-slate-700 dark:text-slate-200 font-bold">{supplier.taxNumber || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-map-marker text-blue-500"></i> Adres
                  </span>
                  <div className="text-slate-700 dark:text-slate-200 font-bold text-sm leading-relaxed">{supplier.address || 'Adres belirtilmedi'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER - FINANCIAL SUMMARY */}
        <div className="lg:col-span-6">
          <div className="h-full bg-white shadow-sm border border-slate-200 rounded-2xl p-4 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h4 className="flex items-center gap-2 m-0 text-slate-800 dark:text-slate-200 font-bold">
                <i className="pi pi-chart-pie !text-blue-500"></i> Finansal Durum
              </h4>
            </div>

            <div className="text-center  bg-slate-50 rounded-2xl py-2 border border-slate-100  mb-4">
              <span className="text-slate-600 dark:text-slate-300 uppercase tracking-widest text-[12px] font-black block mt-2 mb-2">GÜNCEL BAKİYE</span>
              <h2 className={`m-0 text-5xl font-black ${supplier.balance < 0 ? '!text-red-600 dark:!text-red-400' : '!text-emerald-600 dark:!text-emerald-400'}`}>
                {supplier.balance.toLocaleString("tr-TR")} ₺
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4  mt-4">
              <div className="text-center group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Toplam Alış</div>
                <div className="text-slate-800 dark:text-slate-200 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.totalPurchaseAmount.toLocaleString()} ₺</div>
              </div>
              <div className="text-center border-l border-slate-100 group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Alış Sayısı</div>
                <div className="text-slate-800 dark:text-slate-200 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.totalPurchaseCount}</div>
              </div>
              <div className="text-center border-l border-slate-100 group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Toplam Adet</div>
                <div className="text-slate-800 dark:text-slate-200 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.totalQuantity}</div>
              </div>
              <div className="text-center border-l border-slate-100 group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Ort. Fiyat</div>
                <div className="text-slate-800 dark:text-slate-200 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.avgUnitPrice.toLocaleString()} ₺</div>
              </div>
            </div>
            </div>

            

            <div className="flex-1 min-h-[200px] mb-8">
              <Chart type="line" data={chartData} options={chartOptions} height="100%" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button label="Ödeme Yap" icon="pi pi-wallet" className="bg-emerald-600 border-none hover:bg-emerald-700 rounded-xl py-3.5 font-bold shadow-sm transition-all text-sm" onClick={() => {
                setPaymentMethod("Nakit");
                setUseExistingDocument(false);
                setSelectedDocument(null);
                setPaymentAmount(0);
                setSelectedPayableId(null);
                setPaymentDialogVisible(true);
              }} />
              <Button label="Ürün Al" icon="pi pi-shopping-cart" className="!bg-blue-600 border-none hover:!bg-blue-700 rounded-xl py-3.5 font-bold shadow-sm transition-all text-sm" onClick={() => navigate("/suppliers/purchase", { state: { supplierId, supplierName: supplier.name } })} />
              {supplier.relatedCustomerId ? (
                <Button
                  label="Satış Yap"
                  icon="pi pi-download"
                  className="!bg-indigo-600 border-none hover:!bg-indigo-700 rounded-xl py-3.5 font-bold shadow-sm transition-all text-sm"
                  onClick={() => {
                    navigate("/sales/productsale", {
                      state: {
                        preSelectedSupplier: {
                          id: supplierId,
                          name: supplier.name,
                          phone: supplier.phoneNumber,
                        },
                      },
                    });
                  }}
                />
              ) : (
                <Button label="İade Gir" icon="pi pi-refresh" className="!bg-orange-600 border-none hover:!bg-orange-700 rounded-xl py-3.5 font-bold shadow-sm transition-all text-sm" onClick={() => navigate("/suppliers/return", { state: { supplierId, supplierName: supplier.name } })} />
              )}
              {supplier.relatedCustomerId && (
                <Button label="İade Gir" icon="pi pi-refresh" className="!bg-orange-600 border-none hover:!bg-orange-700 rounded-xl py-3.5 font-bold shadow-sm transition-all text-sm" onClick={() => navigate("/suppliers/return", { state: { supplierId, supplierName: supplier.name } })} />
              )}
              <Button label="Ekstre" icon="pi pi-file" className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-none hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl py-3.5 font-bold shadow-sm transition-all text-sm" onClick={() => setShowStatementDialog(true)} />
              {supplier.relatedCustomerId && (
                <Button
                  label="Otomatik FIFO Mahsup"
                  icon="pi pi-sync"
                  className="!bg-purple-600 border-none hover:!bg-purple-700 rounded-xl py-3.5 font-bold shadow-sm transition-all text-white text-sm"
                  onClick={() => {
                    setSelectedOffsetCustomerInvoices([]);
                    setSelectedOffsetSupplierInvoices([]);
                    setGeneralOffsetAmount(0);
                    setShowGeneralOffsetDialog(true);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* RIGHT - TRANSACTION HISTORY (TIMELINE) or UPCOMING PAYMENTS */}
        <div className="lg:col-span-3">
          <div className="h-full bg-white shadow-sm border !border-slate-200 rounded-2xl overflow-hidden flex flex-col">
            {supplier?.useDueTracking ? (
              <TabView className="h-full flex flex-col pt-2" panelContainerClassName="flex-1 overflow-hidden p-0">
                <TabPanel header={<span><i className="pi pi-calendar-plus mr-2 text-xs"></i>Yaklaşanlar</span>} className="h-full flex flex-col">
                  <div className="flex-1 overflow-y-auto px-3 py-4 custom-scrollbar">
                    {upcomingPayments.length === 0 ? (
                      <div className="text-center py-12 !text-slate-400 italic text-sm font-medium">
                        Bekleyen ödeme bulunmuyor.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcomingPayments.map((item) => (
                          <div key={item.id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:border-blue-400 transition-colors cursor-default group">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.transactionDate}</span>
                              <Tag value={item.status} severity={item.status === 'Açık' ? 'danger' : 'warning'} className="text-[8px] font-black" />
                            </div>
                            <div className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-2 leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400">{item.description}</div>
                            <div className="flex justify-between items-end border-t border-slate-200 pt-2">
                              <div className="flex flex-col">
                                <span className="text-[8px] font-black text-slate-400 uppercase">VADE TARİHİ</span>
                                <span className={`text-[10px] font-black ${new Date(item.dueDate.split('.').reverse().join('-')) < new Date() ? 'text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-slate-400'}`}>
                                  {item.dueDate}
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="text-[8px] font-black text-slate-400 uppercase block">KALAN</span>
                                <span className="text-sm font-black text-slate-900 dark:text-white">₺{item.remainingAmount.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabPanel>
                <TabPanel header={<span><i className="pi pi-history mr-2 text-xs"></i>Geçmiş</span>} className="h-full flex flex-col">
                  <div className="flex-1 overflow-y-auto pr-6 custom-scrollbar px-3 py-4">
                    <HistoryTimeline timelineEvents={timelineEvents} />
                  </div>
                </TabPanel>
              </TabView>
            ) : (
              <div className="p-3 flex flex-col h-full">
                <h4 className="flex items-center gap-2 m-0 mb-4 text-slate-800 dark:text-slate-200 font-bold">
                  <i className="pi pi-history !text-blue-500"></i> İşlem Geçmişi
                </h4>
                <div className="flex-1 overflow-y-auto pr-6 custom-scrollbar">
                   <HistoryTimeline timelineEvents={timelineEvents} />
                </div>
              </div>
            )}
          </div>
        </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* PURCHASE HISTORY TABBED TABLE */}
        <div className="lg:col-span-8">
          <Card className="p-0 overflow-hidden">
            <TabView className="supplier-detail-tabs" activeIndex={activeLeftTab} onTabChange={(e) => setActiveLeftTab(e.index)}>
              {viewPreference !== "INVOICE" && (
                <TabPanel header={<span><i className="pi pi-box mr-2"></i>Ürün Bazlı Alışlar</span>}>
                  <div className="bg-white overflow-hidden">
                  <DataTable
                    value={purchaseHistory}
                    paginator rows={10}
                    emptyMessage="Alış kaydı bulunamadı."
                    responsiveLayout="stack"
                    className="p-datatable-sm"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={() => 'hover:bg-slate-50 transition-colors'}
                  >
                    <Column field="date" header="Tarih" sortable className="text-slate-600 py-4 font-medium" />
                    <Column field="productName" header="Ürün" sortable className="text-slate-800 dark:text-slate-200 font-bold" />
                    <Column field="quantity" header="Adet" sortable body={(r) => <span className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-3 py-1 rounded-full text-xs font-bold border border-slate-200 dark:border-slate-700">{r.quantity}</span>} />
                    <Column field="unitPrice" header="Birim Fiyat" sortable body={(r) => <span className="text-slate-600">₺{r.unitPrice.toLocaleString("tr-TR",{
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          })}</span>} />
                    <Column field="totalPrice" header="Toplam" sortable className="text-slate-900 dark:text-white font-black" body={(r) => `₺${r.totalPrice.toLocaleString("tr-TR",{
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          })}`} />
                    <Column
                      header="İşlem"
                      body={(r) => (
                        <Button
                          icon="pi pi-refresh"
                          className="p-button-rounded p-button-warning p-button-text hover:bg-orange-50"
                          onClick={() => {
                            setSelectedReturnRow(r);
                            setReturnQuantity(1);
                            setShowReturnDialog(true);
                          }}
                          tooltip="İade Et"
                          tooltipOptions={{ position: 'top' }}
                        />
                      )}
                      className="w-16"
                    />
                  </DataTable>
                </div>
              </TabPanel>
            )}
              <TabPanel header={<span><i className="pi pi-file mr-2"></i>Faturalar</span>}>
                <div className="bg-white overflow-hidden">
                  <div className="flex items-center justify-between p-3 bg-slate-50 border-b border-slate-100 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox inputId="showOnlyUnpaid" onChange={e => setShowOnlyUnpaid(e.checked)} checked={showOnlyUnpaid} />
                      <label htmlFor="showOnlyUnpaid" className="text-xs font-bold text-slate-600 cursor-pointer">Sadece Ödenmemiş Faturaları Göster</label>
                    </div>
                  </div>
                  <DataTable
                    value={filteredInvoiceHistory}
                    paginator rows={10}
                    emptyMessage="Fatura bulunamadı."
                    responsiveLayout="stack"
                    className="p-datatable-sm supplier-invoice-table"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={(rowData) => {
                      const isUnpaid = rowData.remainingAmount > 0.01;
                      return isUnpaid ? 'unpaid-invoice-row' : '';
                    }}
                  >
                    <Column
                      header="TÜR"
                      body={(rowData) => rowData.type === 'sales' ? (
                        <div className="flex items-center gap-1.5 text-indigo-600 bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/30 px-2 py-1 rounded-lg w-max font-bold text-[10px]">
                          <i className="pi pi-arrow-up-right"></i>
                          <span>SATIŞ</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50/70 dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/30 px-2 py-1 rounded-lg w-max font-bold text-[10px]">
                          <i className="pi pi-arrow-down-left"></i>
                          <span>ALIŞ</span>
                        </div>
                      )}
                      className="w-24 text-[10px] font-bold"
                    />
                    <Column field="invoiceNo" header="Fatura No" sortable className="text-slate-800 dark:text-slate-200 font-bold" />
                    <Column field="date" header="Tarih" sortable className="text-slate-600 py-4 font-medium" />
                    <Column field="totalQuantity" header="Toplam Ürün" sortable body={(r) => <span className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-xs font-bold border border-blue-100 dark:border-blue-900/30">{r.totalQuantity}</span>} />
                    <Column field="totalAmount" header="Toplam Tutar" sortable className="text-slate-900 dark:text-white font-black" body={(r) => `₺${r.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`} />
                    <Column field="paidAmount" header="Ödenen" sortable className="text-emerald-600 font-black" body={(r) => `₺${r.paidAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`} />
                    <Column field="remainingAmount" header="Kalan" sortable className="text-slate-900 font-black" body={(r) => `₺${r.remainingAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`} />
                    <Column field="dueDate" header="Vade Tarihi" sortable className="text-slate-600 font-bold" body={(r) => (
                      <span className={r.type === 'purchase' && r.isDueTrackingActive && new Date(r.dueDate.split('.').reverse().join('-')) < new Date() ? 'text-red-600' : ''}>
                        {r.dueDate || '-'}
                      </span>
                    )} />
                    <Column
                      header="İşlemler"
                      body={(rowData) => rowData.type === 'sales' ? (
                        <div className="flex items-center gap-1">
                          {supplier.relatedCustomerId && rowData.remainingAmount > 0.01 && (
                            <>
                              <Button
                                icon="pi pi-arrow-h"
                                className="p-button-text p-button-info hover:bg-blue-50 rounded-xl w-8 h-8 p-0"
                                tooltip="Karşı Fatura ile Mahsup Et"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => {
                                  setOffsetSourceInvoice(rowData);
                                  setOffsetTargetInvoice(null);
                                  setOffsetAmount(rowData.remainingAmount);
                                  setOffsetMode('sales');
                                  setShowOffsetDialog(true);
                                }}
                              />
                              <Button
                                icon="pi pi-sliders-h"
                                className="p-button-text p-button-help hover:bg-purple-50 rounded-xl w-8 h-8 p-0"
                                tooltip="Otomatik Mahsup Et (FIFO)"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => {
                                  confirmDialog({
                                    message: `${rowData.invoiceNo} nolu satış faturası için otomatik mahsuplaşma başlatılsın mı? Bu işlem FIFO yöntemini kullanarak karşı taraftaki en eski vadeli alış faturalarını kapatacaktır.`,
                                    header: 'Otomatik Mahsuplaşma',
                                    icon: 'pi pi-exclamation-triangle',
                                    acceptLabel: 'Evet, Mahsup Et',
                                    rejectLabel: 'Vazgeç',
                                    accept: () => handleAutoOffset(rowData, 'sales')
                                  });
                                }}
                              />
                            </>
                          )}
                          <Button
                            icon="pi pi-search"
                            className="p-button-rounded p-button-text p-button-secondary hover:bg-slate-100 w-8 h-8 p-0"
                            tooltip="Detay"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/sales/detail/${rowData.id}`)}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          {supplier.relatedCustomerId && rowData.remainingAmount > 0.01 && (
                            <>
                              <Button
                                icon="pi pi-arrow-h"
                                className="p-button-text p-button-info hover:bg-blue-50 rounded-xl w-8 h-8 p-0"
                                tooltip="Karşı Fatura ile Mahsup Et"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => {
                                  setOffsetSourceInvoice(rowData);
                                  setOffsetTargetInvoice(null);
                                  setOffsetAmount(rowData.remainingAmount);
                                  setOffsetMode('purchase');
                                  setShowOffsetDialog(true);
                                }}
                              />
                              <Button
                                icon="pi pi-sliders-h"
                                className="p-button-text p-button-help hover:bg-purple-50 rounded-xl w-8 h-8 p-0"
                                tooltip="Otomatik Mahsup Et (FIFO)"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => {
                                  confirmDialog({
                                    message: `${rowData.invoiceNo} nolu alış faturası için otomatik mahsuplaşma başlatılsın mı? Bu işlem FIFO yöntemini kullanarak karşı taraftaki en eski vadeli satış faturalarını kapatacaktır.`,
                                    header: 'Otomatik Mahsuplaşma',
                                    icon: 'pi pi-exclamation-triangle',
                                    acceptLabel: 'Evet, Mahsup Et',
                                    rejectLabel: 'Vazgeç',
                                    accept: () => handleAutoOffset(rowData, 'purchase')
                                  });
                                }}
                              />
                            </>
                          )}
                          <Button
                            icon="pi pi-ellipsis-v"
                            className="p-button-text p-button-secondary hover:bg-slate-100 rounded-xl w-8 h-8 p-0"
                            tooltip="Diğer İşlemler"
                            tooltipOptions={{ position: 'top' }}
                            onClick={(e) => {
                              setSelectedInvoice(rowData);
                              menuRef.current.toggle(e);
                            }}
                          />
                        </div>
                      )}
                    />
                  </DataTable>
                </div>
              </TabPanel>
              <TabPanel header={<span><i className="pi pi-refresh mr-2"></i>İadeler</span>}>
                <div className="bg-white overflow-hidden">
                  <DataTable
                    value={returnHistory}
                    paginator rows={10}
                    emptyMessage="İade kaydı bulunamadı."
                    responsiveLayout="stack"
                    className="p-datatable-sm"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={(rowData) => ({
                      '!bg-red-50/80': rowData.isDueTrackingActive && rowData.remainingAmount > 0,
                      'hover:bg-slate-50 transition-colors': true
                    })}
                  >
                    <Column field="date" header="Tarih" sortable className="text-slate-600 py-4 font-medium" />
                    <Column field="invoiceNo" header="Fatura No" sortable className="text-slate-800 dark:text-slate-200 font-bold" />
                    <Column field="totalQuantity" header="Toplam Ürün" sortable body={(r) => <span className="bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 px-3 py-1 rounded-full text-xs font-bold border border-orange-100 dark:border-orange-900/30">{r.totalQuantity}</span>} />
                    <Column field="totalAmount" header="Toplam Tutar" sortable className="text-slate-900 dark:text-white font-black" body={(r) => (
                      <span
                        className="cursor-help inline-block"
                        onMouseEnter={(e) => {
                          e.currentTarget.setAttribute(
                            "data-pr-tooltip",
                            `Kdv Dahil hesaplanmıştır. İade tutarı, iade edilen ürünlerin toplam fiyatının yanı sıra ilgili KDV tutarını da içermektedir.`
                          );

                          tooltipRef.current?.show(e);
                        }}
                        onMouseLeave={(e) => {
                          tooltipRef.current?.hide(e);
                        }}
                      >
                        ₺
                        {r.totalAmount?.toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    )}/>
                    <Column field="description" header="Açıklama" className="text-slate-500 text-sm" />
                    <Column
                        header="İşlem"
                        body={(r) => (
                            <Button
                                icon="pi pi-trash"
                                className="p-button-rounded p-button-text p-button-danger p-0 w-8 h-8"
                                onClick={() => handleDeleteReturn(r)}
                                tooltip="İadeyi İptal Et"
                                tooltipOptions={{ position: 'top' }}
                            />
                        )}
                        className="w-16"
                    />
                  </DataTable>
                </div>
              </TabPanel>
            </TabView>
          </Card>
        </div>

        {/* Ödeme Geçmişi SIDEBAR PANEL */}
        <div className="lg:col-span-4 bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden flex flex-col h-full">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="bg-emerald-500 w-1.5 h-5 rounded-full"></div>
                    <h2 className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest m-0">Ödeme Geçmişi</h2>
                </div>
                <Tag value={`${paymentHistory.length} İŞLEM`} className="bg-emerald-600 text-[10px] font-black" />
            </div>

            <div className="p-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                    <span className="p-input-icon-left flex-1 min-w-[120px]">
                        <i className="pi pi-search text-xs" />
                        <InputText
                            type="search"
                            onInput={(e) => setPaymentFilters({...paymentFilters, global: { value: e.target.value, matchMode: "contains" }})}
                            placeholder="Ödeme ara..."
                            className="w-full rounded-lg text-xs py-2"
                        />
                    </span>
                    <Dropdown
                        value={paymentFilters.paymentMethod.value}
                        options={[{label: 'Tüm Yöntemler', value: null}, ...paymentMethods, {label: 'Bakiye Düzenleme', value: 'Bakiye Düzenleme'}]}
                        onChange={(e) => setPaymentFilters({...paymentFilters, paymentMethod: { value: e.value, matchMode: "equals" }})}
                        placeholder="Yöntem"
                        className="w-[110px] rounded-lg text-xs"
                        appendTo={mounted ? document.body : "self"}
                    />
                    <Calendar
                        value={paymentFilters.date.value ? (function(){
                            const p = paymentFilters.date.value.split('.');
                            return new Date(p[2], p[1]-1, p[0]);
                        })() : null}
                        onChange={(e) => {
                            if (!e.value) {
                                setPaymentFilters({...paymentFilters, date: { value: null, matchMode: "contains" }});
                                return;
                            }
                            const day = String(e.value.getDate()).padStart(2, '0');
                            const month = String(e.value.getMonth() + 1).padStart(2, '0');
                            const year = e.value.getFullYear();
                            const val = `${day}.${month}.${year}`;
                            setPaymentFilters({...paymentFilters, date: { value: val, matchMode: "contains" }});
                        }}
                        dateFormat="dd.mm.yy"
                        placeholder="Tarih"
                        className="w-[100px] rounded-lg text-xs"
                        locale="tr"
                        showButtonBar
                        appendTo={mounted ? document.body : "self"}
                    />
                </div>

                <DataTable
                    value={paymentHistory}
                    paginator rows={10}
                    filters={paymentFilters}
                    globalFilterFields={['date', 'description', 'amount', 'paymentMethod']}
                    emptyMessage="Ödeme bulunamadı."
                    responsiveLayout="scroll"
                    className="p-datatable-sm flex-1"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={() => 'hover:bg-emerald-50/20 transition-colors'}
                >
                    <Column field="date" header="TARİH" sortable className="text-slate-500 text-[10px] font-bold" />
                    <Column field="paymentMethod" header="YÖNTEM" sortable body={(r) => (
                         <div className="flex flex-col">
                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase w-fit ${r.paymentMethod === 'Bakiye Düzenleme' ? '!bg-orange-100 dark:!bg-orange-950/30 text-orange-700 dark:text-orange-300' : '!bg-emerald-100 dark:!bg-emerald-950/30 text-emerald-700 dark:text-emerald-300'}`}>
                                {r.paymentMethod}
                            </span>
                            <span className="text-[8px] text-slate-400 font-bold mt-0.5 italic truncate max-w-[100px]">{r.description}</span>
                        </div>
                    )} />
                    <Column field="amount" header="TUTAR" sortable body={(r) => <span className="font-black text-xs text-slate-900 dark:text-white">₺{r.amount.toLocaleString("tr-TR",{
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          })}</span>} />
                    <Column
                        header="Aksiyon"
                        body={(r) => (
                            <div className="flex gap-1">
                                <Button
                                    icon="pi pi-pencil"
                                    className="p-button-rounded p-button-text p-button-warning p-0 w-8 h-8"
                                    onClick={() => {
                                        const dateParts = r.date.split('.');
                                        const parsedDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);

                                        let parsedDueDate = null;
                                        if (r.dueDate) {
                                            const dParts = r.dueDate.split('.');
                                            parsedDueDate = new Date(dParts[2], dParts[1] - 1, dParts[0]);
                                        }

                                        let rawMethod = r.paymentMethod || (r.description?.includes(' - ') ? r.description.split(' - ')[0].trim() : 'Nakit');

                                        // Map Endorsements to standard Çek/Senet buttons for the UI grid selection
                                        if (rawMethod === 'ChequeEndorse' || rawMethod === 'Çek Cirosu') rawMethod = 'Çek';
                                        if (rawMethod === 'PromissoryEndorse' || rawMethod === 'Senet Cirosu') rawMethod = 'Senet';

                                        const matchedMethod = paymentMethods.find(m => m.value === rawMethod || m.label === rawMethod)?.value || 'Nakit';

                                        // Clean up description (remove extra info blocks to avoid duplication)
                                        let cleanDesc = r.description?.includes(' - ') ? r.description.split(' - ').slice(1).join(' - ') : (r.description || '');
                                        cleanDesc = cleanDesc.split('\n[')[0].trim();

                                        setEditingPayment({
                                            id: r.id,
                                            amount: Math.abs(r.amount),
                                            paymentDate: parsedDate,
                                            description: cleanDesc,
                                            paymentMethod: matchedMethod,
                                            financialAccountId: r.financialAccountId,
                                            bankName: r.bankName,
                                            bankBranch: r.bankBranch,
                                            checkNumber: r.checkNumber,
                                            dueDate: parsedDueDate,
                                            drawer: r.drawer,
                                            guarantor: r.guarantor
                                        });
                                        setPaymentMethod(matchedMethod);
                                        setShowEditPaymentDialog(true);
                                    }}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-rounded p-button-text p-button-danger p-0 w-8 h-8"
                                    onClick={() => handleDeletePayment(r)}
                                />
                            </div>
                        )}
                    />
                </DataTable>
            </div>
        </div>
      </div>

      {/* INVOICE DETAIL DIALOG */}
      <Dialog
        header={
          <div className="flex items-center gap-2">
            <i className="pi pi-file text-blue-500"></i>
            <span>Fatura Detayı {invoiceDetail?.invoiceNo ? `- ${invoiceDetail.invoiceNo}` : ""}</span>
          </div>
        }
        visible={detailDialogVisible}
        onHide={() => {
          setDetailDialogVisible(false);
          setInvoiceDetail(null);
        }}
        style={{ width: "700px" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        className="shadow-xl rounded-xl"
        appendTo={mounted ? document.body : "self"}
      >
        {loadingDetail ? (
          <div className="p-4">
            <Skeleton height="2rem" className="mb-2" />
            <Skeleton height="10rem" />
          </div>
        ) : invoiceDetail ? (
          <div className="space-y-4">
             <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">FATURA NO</span>
                    <span className="font-bold text-slate-700">{invoiceDetail.invoiceNo}</span>
                </div>
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">TARİH</span>
                    <span className="font-bold text-slate-700">{new Date(invoiceDetail.purchaseDate).toLocaleDateString("tr-TR")}</span>
                </div>
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">ÖDEME TİPİ</span>
                    <span className="font-bold text-slate-700">{invoiceDetail.paymentType === 1 ? "Nakit" : "Vadeli"}</span>
                </div>
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">TOPLAM TUTAR</span>
                    <span className="font-bold text-emerald-600">₺{invoiceDetail.totalInclVat.toLocaleString("tr-TR")}</span>
                </div>
             </div>

             <DataTable
                value={invoiceDetail.lines}
                className="p-datatable-sm"
                responsiveLayout="scroll"
                emptyMessage="Ürün bulunamadı."
             >
                <Column field="productName" header="Ürün Adı" className="font-bold" />
                <Column field="quantity" header="Miktar" body={(r) => `${r.quantity} ${r.unit}`} />
                <Column field="unitPrice" header="Birim Fiyat (KDV Hariç)" body={(r) => `₺${r.unitPrice.toLocaleString("tr-TR")}`} />
                <Column header="Toplam" body={(r) => `₺${(r.quantity * r.unitPrice).toLocaleString("tr-TR")}`} className="font-black text-slate-900" />
             </DataTable>

             {invoiceDetail.description && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 rounded-lg text-sm border border-blue-100 dark:border-blue-900/30">
                    <i className="pi pi-info-circle mr-2"></i>
                    {invoiceDetail.description}
                </div>
             )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">Detay yüklenemedi.</div>
        )}
      </Dialog>

      {/* MODERN EDIT PAYMENT SIDEBAR */}
      <Sidebar
        visible={showEditPaymentDialog}
        onHide={() => setShowEditPaymentDialog(false)}
        position="right"
        style={{ width: `${sidebarWidth}px`, maxWidth: '100vw' }}
        className="p-0 border-none shadow-2xl relative"
        content={({ hide }) => (
            <div className="h-full flex flex-col bg-slate-50 relative">
                {/* Resize Handle */}
                <div
                  onMouseDown={startResizing}
                  className="absolute left-0 top-0 w-1 h-full cursor-col-resize hover:bg-amber-500/30 transition-colors z-[100] group"
                >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-8 bg-slate-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>

                {/* Header Section */}
                <div className="bg-slate-900 h-1/5 py-3 px-8 relative overflow-hidden shrink-0 flex flex-col justify-center">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full -mr-24 -mt-24 blur-3xl"></div>
                    <button onClick={hide} 
                    className="absolute top-3 right-6 z-20  rounded-full border border-white/10 bg-white/5 text-white/70 backdrop-blur-md hover:bg-white/30 hover:text-white hover:border-white/20 transition-all duration-200"
                    aria-label="Kapat"
                    ><i className="pi pi-times text-lg"></i></button>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-2 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                                <i className="pi pi-pencil text-white text-lg"></i>
                            </div>
                            <div>
                                <span className="text-[9px] font-black text-amber-500 uppercase tracking-[0.2em] block">ÖDEME DÜZENLE</span>
                                <h2 className="text-xl font-black text-white leading-tight truncate max-w-[400px]">{supplier?.name}</h2>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">GÜNCEL BAKİYE</span>
                                <div className="text-lg font-black text-white">{(supplier?.balance || 0).toLocaleString()} <span className="text-amber-500 text-xs">₺</span></div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">YENİ BAKİYE</span>
                                <div className="text-lg font-black text-white">
                                    {( (supplier?.balance || 0) + (paymentHistory.find(p => p.id === editingPayment?.id)?.amount || 0) - (editingPayment?.amount || 0)).toLocaleString()}
                                    <span className="text-amber-500 text-xs"> ₺</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body Content */}
                <div className="h-3/5 overflow-y-auto custom-scrollbar px-8 py-6 space-y-8 flex-1">
                    {editingPayment && (
                        <>
                            {/* Payment Method Selector with Categories */}
                            <section className="space-y-6">
                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <i className="pi pi-th-large text-amber-500"></i> ÖDEME YÖNTEMİ SEÇİN
                                </label>

                                {["Likit", "Dijital", "Evrak"].map(cat => (
                                    <div key={cat} className="space-y-3">
                                        <div className="flex items-center gap-3 px-1">
                                            <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.1em]">{cat}</span>
                                            <div className="flex-1 h-px bg-slate-400"></div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {paymentMethods
                                                .filter(m => m.category === cat )
                                                .map((m) => (
                                                <button
                                                    key={m.value}
                                                    onClick={() => {
                                                        setEditingPayment({...editingPayment, paymentMethod: m.value});
                                                        setPaymentMethod(m.value);
                                                    }}
                                                    className={`group relative flex items-center gap-3 p-3 rounded-2xl border-2 transition-all duration-300 text-left ${
                                                        editingPayment.paymentMethod === m.value
                                                        ? 'border-amber-500 bg-white shadow-xl shadow-amber-500/5'
                                                        : 'border-slate-200 bg-white hover:border-slate-500'
                                                    }`}
                                                >
                                                    <div className={`w-4 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                                                        editingPayment.paymentMethod === m.value
                                                        ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20'
                                                        : 'bg-slate-200 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-500'
                                                    }`}>
                                                        <i className={`pi ${m.icon} text-lg`}></i>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-0.5">
                                                            <span className={`text-[12px] font-black uppercase tracking-tight leading-none ${editingPayment.paymentMethod === m.value ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                                                                {m.label}
                                                            </span>
                                                            {editingPayment.paymentMethod === m.value && (
                                                                <i className="pi pi-check-circle text-amber-500 text-xs animate-scale-in"></i>
                                                            )}
                                                        </div>
                                                        <p className={`text-[10px] leading-tight font-medium truncate ${editingPayment.paymentMethod === m.value ? 'text-amber-600/80' : 'text-slate-400'}`}>
                                                            {m.desc}
                                                        </p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </section>

                            {/* Document Details for Edit */}
                            {(editingPayment.paymentMethod === "Çek" || editingPayment.paymentMethod === "Senet") && (
                                <section className="bg-amber-50/50 dark:!bg-amber-950/20 p-6 rounded-3xl space-y-5 border border-amber-100 dark:border-amber-900/30 animate-fade-in">
                                    <div className="text-[11px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                        <i className="pi pi-file-edit"></i> BELGE DETAYLARI
                                    </div>
                                    <div className="space-y-4">
                                        {editingPayment.paymentMethod === 'Çek' ? (
                                            <>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                                        Bağlı Banka Hesabı (Sistem Kayıtlı)
                                                    </label>
                                                    <Dropdown
                                                        value={editingPayment.financialAccountId}
                                                        options={bankAccountOptions}
                                                        onChange={(e) => {
                                                            const opt = bankAccountOptions.find(o => o.value === e.value);
                                                            setEditingPayment({
                                                                ...editingPayment,
                                                                financialAccountId: e.value,
                                                                bankName: editingPayment.bankName || opt?.name || ''
                                                            });
                                                        }}
                                                        placeholder="Banka Hesabı Seçin (Opsiyonel)"
                                                        showClear
                                                        className="w-full rounded-xl border-slate-200 font-bold"
                                                        appendTo={mounted ? document.body : "self"}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Banka Adı</label>
                                                        <InputText value={editingPayment.bankName || ''} onChange={e => setEditingPayment({...editingPayment, bankName: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Şube</label>
                                                        <InputText value={editingPayment.bankBranch || ''} onChange={e => setEditingPayment({...editingPayment, bankBranch: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade Tarihi</label>
                                                        <Calendar value={editingPayment.dueDate} onChange={e => setEditingPayment({...editingPayment, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Çek Numarası</label>
                                                        <InputText value={editingPayment.checkNumber || ''} onChange={e => setEditingPayment({...editingPayment, checkNumber: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Keşideci</label>
                                                    <InputText value={editingPayment.drawer || ''} onChange={e => setEditingPayment({...editingPayment, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Borçlu Adı</label>
                                                    <InputText value={editingPayment.drawer || ''} onChange={e => setEditingPayment({...editingPayment, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade Tarihi</label>
                                                        <Calendar value={editingPayment.dueDate} onChange={e => setEditingPayment({...editingPayment, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Kefil</label>
                                                        <InputText value={editingPayment.guarantor || ''} onChange={e => setEditingPayment({...editingPayment, guarantor: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </section>
                            )}

                            {/* Account & Date Section */}
                            <section className="grid grid-cols-1 gap-6">
                                {(filteredAccounts.length > 0 || paymentMethod === "Sanal POS") && (
                                    <div className="space-y-2 animate-fade-in">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <i className="pi pi-credit-card text-amber-500"></i> HESAP SEÇİMİ
                                        </label>
                                        {filteredAccounts.length > 0 ? (
                                            <Dropdown
                                                value={editingPayment.financialAccountId}
                                                options={filteredAccounts}
                                                optionLabel="name"
                                                optionValue="id"
                                                onChange={(e) => setEditingPayment({...editingPayment, financialAccountId: e.value})}
                                                placeholder="Hesap seçin..."
                                                className="w-full rounded-2xl border-slate-200 font-bold"
                                                appendTo={mounted ? document.body : "self"}
                                            />
                                        ) : (
                                            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/30 rounded-2xl text-amber-700 dark:text-amber-300 text-xs font-semibold">
                                                Bu tedarikçi için henüz bir Sanal POS hesabı bulunmuyor. Kaydedildiğinde otomatik olarak oluşturulacaktır.
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <i className="pi pi-calendar text-amber-500"></i> İŞLEM TARİHİ
                                    </label>
                                    <Calendar
                                        value={editingPayment.paymentDate}
                                        onChange={(e) => setEditingPayment({...editingPayment, paymentDate: e.value})}
                                        dateFormat="dd.mm.yy"
                                        locale="tr"
                                        showIcon
                                        appendTo={mounted ? document.body : "self"}
                                        className="w-full"
                                        inputClassName="font-bold py-3 rounded-2xl border-slate-200"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <i className="pi pi-align-left text-amber-500"></i> AÇIKLAMA
                                    </label>
                                    <InputTextarea
                                        value={editingPayment.description}
                                        onChange={(e) => setEditingPayment({...editingPayment, description: e.target.value})}
                                        placeholder="Opsiyonel açıklama..."
                                        rows={2}
                                        className="w-full p-4 rounded-2xl border-slate-200 font-medium text-sm"
                                        autoResize
                                    />
                                </div>
                            </section>
                        </>
                    )}
                </div>

                {/* Fixed Footer */}
                <div className="h-1/5 px-8 py-3 bg-white border-t border-slate-100 space-y-3 shrink-0 flex flex-col justify-center">
                    <div className="relative">
                        <span className="absolute -top-2 left-5 px-2 bg-white text-[9px] font-black text-amber-600 uppercase tracking-widest z-10 rounded-full border border-amber-100">ÖDEME TUTARI</span>
                        <InputNumber
                            value={editingPayment?.amount}
                            onValueChange={(e) => setEditingPayment({...editingPayment, amount: e.value})}
                            mode="decimal"
                            minFractionDigits={2}
                            className="w-full"
                            inputClassName="font-black text-2xl py-4 px-6 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:border-amber-500 transition-all text-slate-900 dark:text-white"
                            placeholder="0.00"
                        />
                        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-xl font-black text-slate-300 pointer-events-none">₺</div>
                    </div>
                    <Button
                        label="DEĞİŞİKLİKLERİ KAYDET"
                        icon="pi pi-check-circle"
                        className="w-full bg-slate-900 hover:bg-black border-none py-4 rounded-2xl font-black text-xs tracking-widest shadow-xl shadow-slate-900/20 text-white transition-all transform hover:scale-[1.02] active:scale-95"
                        onClick={handleUpdatePayment}
                        loading={submittingPayment}
                        disabled={!editingPayment || editingPayment.amount <= 0}
                    />
                </div>
            </div>
        )}
      />

      {/* MODERN NEW PAYMENT SIDEBAR */}
      <Sidebar
        visible={paymentDialogVisible}
        onHide={() => {
          setPaymentDialogVisible(false);
          setSelectedPayableId(null);
          setPaymentAmount(0);
          setPaymentDescription("");
        }}
        position="right"
        style={{ width: `${sidebarWidth}px`, maxWidth: '100vw' }}
        className="p-0 border-none shadow-2xl relative"
        onShow={() => setTimeout(() => paymentAmountRef.current?.focus(), 50)}
        content={({ hide }) => (
            <div className="h-full flex flex-col bg-slate-50 relative">
                {/* Resize Handle */}
                <div
                  onMouseDown={startResizing}
                  className="absolute left-0 top-0 w-1 h-full cursor-col-resize hover:bg-emerald-500/30 transition-colors z-[100] group"
                >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-8 bg-slate-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>

                {/* Header Section */}
                <div className="bg-slate-900 h-1/5 py-3 px-8 relative overflow-hidden shrink-0 flex flex-col justify-center">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -mr-24 -mt-24 blur-3xl"></div>
                    <button onClick={hide} 
                    className="absolute top-3 right-6 z-20  rounded-full border border-white/10 bg-white/5 text-white/70 backdrop-blur-md hover:bg-white/30 hover:text-white hover:border-white/20 transition-all duration-200"
                    aria-label="Kapat"
                    
                    ><i className="pi pi-times text-lg"></i></button>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-2 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-xl shadow-emerald-500/20">
                                <i className="pi pi-wallet text-white text-lg"></i>
                            </div>
                            <div>
                                <span className="text-[9px] font-black text-emerald-500 uppercase tracking-[0.2em] block">YENİ ÖDEME</span>
                                <h2 className="text-xl font-black text-white leading-tight truncate max-w-[400px]">{supplier?.name}</h2>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">GÜNCEL BAKİYE</span>
                                <div className="text-lg font-black text-white">{(supplier?.balance || 0).toLocaleString()} <span className="text-emerald-500 text-xs">₺</span></div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">PROJEKSİYON</span>
                                <div className="text-lg font-black text-white">
                                    {((supplier?.balance || 0) - (paymentAmount || 0)).toLocaleString()}
                                    <span className="text-emerald-500 text-xs"> ₺</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body Content */}
                <div className="h-3/5 overflow-y-auto custom-scrollbar px-8 py-6 space-y-8 flex-1">
                    {/* Payment Method Selector with Categories */}
                    <section className="space-y-6">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <i className="pi pi-th-large text-emerald-500"></i> ÖDEME YÖNTEMİ SEÇİN
                        </label>

                        {["Likit", "Dijital", "Evrak"].map(cat => (
                            <div key={cat} className="space-y-3">
                                <div className="flex items-center gap-3 px-1">
                                    <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.1em]">{cat}</span>
                                    <div className="flex-1 h-px bg-slate-400"></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {paymentMethods
                                        .filter(m => m.category === cat)
                                        .map((m) => (
                                        <button
                                            key={m.value}
                                            onClick={() => {
                                                setPaymentMethod(m.value);
                                                setUseExistingDocument(false);
                                                setSelectedDocument(null);
                                            }}
                                            className={`group relative flex items-center gap-3 p-3 rounded-2xl border-2 transition-all duration-300 text-left ${
                                                paymentMethod === m.value
                                                ? 'border-emerald-500 bg-white shadow-xl shadow-emerald-500/5'
                                                : 'border-slate-200 bg-white hover:border-slate-500'
                                            }`}
                                        >
                                            <div className={`w-4 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                                                paymentMethod === m.value
                                                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                                                : 'bg-slate-200 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-500'
                                            }`}>
                                                <i className={`pi ${m.icon} text-lg`}></i>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-0.5">
                                                    <span className={`text-[12px] font-black uppercase tracking-tight leading-none ${paymentMethod === m.value ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                                                        {m.label}
                                                    </span>
                                                    {paymentMethod === m.value && (
                                                        <i className="pi pi-check-circle text-emerald-500 text-xs animate-scale-in"></i>
                                                    )}
                                                </div>
                                                <p className={`text-[10px] leading-tight font-medium truncate ${paymentMethod === m.value ? 'text-emerald-600/80' : 'text-slate-400'}`}>
                                                    {m.desc}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>

                    {/* Document Details Logic */}
                    {(paymentMethod === "ChequeEndorse" || paymentMethod === "PromissoryEndorse" || paymentMethod === "Çek" || paymentMethod === "Senet") && (
                        <section className="bg-emerald-50/50 dark:!bg-emerald-950/20 p-6 rounded-3xl space-y-5 border border-emerald-100 dark:border-emerald-900/30 animate-fade-in">
                            <div className="flex justify-between items-center">
                                <div className="text-[11px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                                    <i className="pi pi-file-edit"></i> BELGE BİLGİSİ
                                </div>
                                {(paymentMethod === "Çek" || paymentMethod === "Senet") && (
                                    <SelectButton
                                        value={useExistingDocument ? 'existing' : 'new'}
                                        options={[{label: 'YENİ', value: 'new'}, {label: 'PORTFÖY', value: 'existing'}]}
                                        onChange={(e) => { if (e.value) { setUseExistingDocument(e.value === 'existing'); setSelectedDocument(null); } }}
                                        className="custom-selectbutton modern-small shadow-sm"
                                    />
                                )}
                            </div>

                            {((paymentMethod === "ChequeEndorse" || paymentMethod === "PromissoryEndorse") || useExistingDocument) ? (
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">PORTFÖYDEN SEÇ</label>
                                    <Dropdown
                                        value={selectedDocument}
                                        onChange={(e) => { setSelectedDocument(e.value); setPaymentAmount(e.value.amount); }}
                                        options={paymentMethod.includes('Cheque') || paymentMethod === 'Çek' ? (paymentMethod === 'Çek' ? ownPortfolioCheques : portfolioCheques) : (paymentMethod === 'Senet' ? ownPortfolioNotes : portfolioNotes)}
                                        optionLabel={(option) => `${option.bankName || option.drawer} - ${option.amount.toLocaleString()} ₺`}
                                        placeholder="Belge seçin..."
                                        className="w-full rounded-xl border-slate-200 font-bold"
                                        appendTo={mounted ? document.body : "self"}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {paymentMethod === 'Çek' ? (
                                        <>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                                    Bağlı Banka Hesabı (Sistem Kayıtlı)
                                                </label>
                                                <Dropdown
                                                    value={selectedAccountId}
                                                    options={bankAccountOptions}
                                                    onChange={(e) => {
                                                        setSelectedAccountId(e.value);
                                                        const opt = bankAccountOptions.find(o => o.value === e.value);
                                                        if (opt) {
                                                            setChequeDetails(prev => ({
                                                                ...prev,
                                                                bankName: prev.bankName || opt.name
                                                            }));
                                                        }
                                                    }}
                                                    placeholder="Banka Hesabı Seçin (Opsiyonel)"
                                                    showClear
                                                    className="w-full rounded-xl border-slate-200 font-bold"
                                                    appendTo={mounted ? document.body : "self"}
                                                />
                                                <span className="text-[10px] text-slate-400">Çekin kesildiği banka hesabını seçerseniz, vadesinde ödeme takviminde ve ödeme işlemlerinde bu hesapla ilişkilendirilir.</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Banka Adı</label><InputText value={chequeDetails.bankName} onChange={e => setChequeDetails({...chequeDetails, bankName: e.target.value})} placeholder="Örn: Garanti BBVA" className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Şube</label><InputText value={chequeDetails.bankBranch} onChange={e => setChequeDetails({...chequeDetails, bankBranch: e.target.value})} placeholder="Örn: Kadıköy Şb." className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade</label><Calendar value={chequeDetails.dueDate} onChange={e => setChequeDetails({...chequeDetails, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" /></div>
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">No</label><InputText value={chequeDetails.checkNumber} onChange={e => setChequeDetails({...chequeDetails, checkNumber: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            </div>
                                            <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Keşideci</label><InputText value={chequeDetails.drawer} onChange={e => setChequeDetails({...chequeDetails, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Borçlu</label><InputText value={promissoryDetails.drawer} onChange={e => setPromissoryDetails({...promissoryDetails, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade</label><Calendar value={promissoryDetails.dueDate} onChange={e => setPromissoryDetails({...promissoryDetails, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" /></div>
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Kefil</label><InputText value={promissoryDetails.guarantor} onChange={e => setPromissoryDetails({...promissoryDetails, guarantor: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    {/* Account & Date Section */}
                    <section className="grid grid-cols-1 gap-6">
                        {(filteredAccounts.length > 0 || paymentMethod === "Sanal POS") && (
                            <div className="space-y-2 animate-fade-in">
                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <i className="pi pi-credit-card text-emerald-500"></i> HESAP SEÇİMİ
                                </label>
                                {filteredAccounts.length > 0 ? (
                                    <Dropdown
                                        value={selectedAccountId}
                                        options={filteredAccounts}
                                        optionLabel="name"
                                        optionValue="id"
                                        onChange={(e) => setSelectedAccountId(e.value)}
                                        placeholder="İşlem yapılacak hesabı seçin"
                                        className="w-full rounded-2xl border-slate-200 font-bold"
                                        appendTo={mounted ? document.body : "self"}
                                    />
                                ) : (
                                    <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/30 rounded-2xl text-amber-700 dark:text-amber-300 text-xs font-semibold">
                                        Bu tedarikçi için henüz bir Sanal POS hesabı bulunmuyor. Kaydedildiğinde otomatik olarak oluşturulacaktır.
                                    </div>
                                )}
                            </div>
                        )}
                        {paymentMethod === "Kredi Kartı" && (
                            <div className="bg-emerald-50/30 border border-emerald-100/50 rounded-2xl p-4 space-y-4 animate-fade-in">
                                <div className="text-[10px] font-black text-emerald-700 uppercase tracking-widest flex items-center gap-2">
                                    <i className="pi pi-clock"></i> TAKSİT SEÇENEKLERİ
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">TAKSİT SAYISI</label>
                                        <InputNumber
                                            value={installmentCount}
                                            onValueChange={(e) => setInstallmentCount(e.value || 1)}
                                            min={1}
                                            max={36}
                                            showButtons
                                            buttonLayout="horizontal"
                                            decrementButtonClassName="p-button-secondary p-button-outlined"
                                            incrementButtonClassName="p-button-secondary p-button-outlined"
                                            incrementButtonIcon="pi pi-plus"
                                            decrementButtonIcon="pi pi-minus"
                                            className="w-full"
                                            inputClassName="text-center font-bold"
                                        />
                                    </div>
                                    {installmentCount > 1 && (
                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">İLK TAKSİT TARİHİ</label>
                                            <Calendar
                                                value={firstInstallmentDate}
                                                onChange={(e) => setFirstInstallmentDate(e.value)}
                                                dateFormat="dd.mm.yy"
                                                locale="tr"
                                                showIcon
                                                appendTo={mounted ? document.body : "self"}
                                                className="w-full"
                                                inputClassName="font-bold py-3 rounded-2xl border-slate-200"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <i className="pi pi-calendar text-emerald-500"></i> İŞLEM TARİHİ
                            </label>
                            <Calendar
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.value)}
                                dateFormat="dd.mm.yy"
                                locale="tr"
                                showIcon
                                appendTo={mounted ? document.body : "self"}
                                className="w-full"
                                inputClassName="font-bold py-3 rounded-2xl border-slate-200"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <i className="pi pi-align-left text-emerald-500"></i> AÇIKLAMA
                            </label>
                            <InputTextarea
                                value={paymentDescription}
                                onChange={(e) => setPaymentDescription(e.target.value)}
                                placeholder="Opsiyonel açıklama..."
                                rows={2}
                                className="w-full p-4 rounded-2xl border-slate-200 font-medium text-sm"
                                autoResize
                            />
                        </div>
                    </section>
                </div>

                {/* Fixed Footer */}
                <div className="h-1/5 px-8 py-3 bg-white border-t border-slate-100 space-y-3 shrink-0 flex flex-col justify-center">
                    <div className="relative">
                        <span className="absolute -top-2 left-5 px-2 bg-white text-[9px] font-black text-emerald-600 uppercase tracking-widest z-10 rounded-full border border-emerald-100">ÖDEME TUTARI</span>
                        <InputNumber
                            inputRef={paymentAmountRef}
                            value={paymentAmount}
                            onValueChange={(e) => setPaymentAmount(e.value)}
                            mode="decimal"
                            minFractionDigits={2}
                            className="w-full"
                            inputClassName="font-black text-2xl py-3 px-6 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:border-emerald-500 transition-all text-slate-900 dark:text-white"
                            placeholder="0.00"
                        />
                        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-xl font-black text-slate-300 pointer-events-none">₺</div>
                    </div>
                    <Button
                        label="İŞLEMİ ONAYLA"
                        icon="pi pi-check-circle"
                        className="w-full bg-slate-900 hover:bg-black border-none py-4 rounded-2xl font-black text-xs tracking-widest shadow-xl shadow-slate-900/20 text-white transition-all transform hover:scale-[1.02] active:scale-95"
                        onClick={handlePayment}
                        loading={submittingPayment}
                        disabled={paymentAmount <= 0 && paymentMethod !== "ChequeEndorse" && paymentMethod !== "PromissoryEndorse" && !useExistingDocument}
                    />
                </div>
            </div>
        )}
      />

      <Dialog
        header="Fatura Mahsuplaşma (Virman/Offset)"
        visible={showOffsetDialog}
        style={{ width: '500px' }}
        modal
        onHide={() => setShowOffsetDialog(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button
              label="Vazgeç"
              icon="pi pi-times"
              className="p-button-text p-button-secondary !rounded-xl font-bold"
              onClick={() => setShowOffsetDialog(false)}
            />
            <Button
              label="Mahsup Et"
              icon="pi pi-check"
              loading={offsetSubmitting}
              className="p-button-primary !rounded-xl font-bold !bg-indigo-600 !border-indigo-600 shadow-md shadow-indigo-500/20"
              onClick={handleOffsetSubmit}
            />
          </div>
        )}
      >
        {offsetSourceInvoice && (
          <div className="flex flex-col gap-4 p-fluid">
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
              <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest block mb-1">Kaynak Fatura</span>
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-slate-800">
                  {offsetMode === 'sales' ? offsetSourceInvoice.saleNo : offsetSourceInvoice.invoiceNo}
                </span>
                <span className="text-xs font-bold text-slate-500">
                  Kalan: ₺{((offsetMode === 'sales' ? (offsetSourceInvoice.totalAmount - offsetSourceInvoice.paidAmount) : offsetSourceInvoice.remainingAmount)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div>
              <label className="text-xs font-black text-slate-600 block mb-2">Mahsup Edilecek Hedef Faturayı Seçin</label>
              <Dropdown
                value={offsetTargetInvoice}
                onChange={(e) => {
                  setOffsetTargetInvoice(e.value);
                  if (e.value) {
                    const sourceRemaining = offsetMode === 'sales'
                      ? (offsetSourceInvoice.totalAmount - offsetSourceInvoice.paidAmount)
                      : offsetSourceInvoice.remainingAmount;
                    const targetRemaining = offsetMode === 'sales'
                      ? e.value.remainingAmount
                      : (e.value.totalAmount - e.value.paidAmount);
                    setOffsetAmount(Math.min(sourceRemaining, targetRemaining));
                  }
                }}
                options={offsetMode === 'sales'
                  ? invoiceHistory.filter(inv => inv.isDueTrackingActive && inv.remainingAmount > 0.01)
                  : customerInvoices.filter(inv => inv.totalAmount - inv.paidAmount > 0.01)
                }
                optionLabel={(inv) => `${inv.saleNo || inv.invoiceNo} - ₺${(inv.remainingAmount || (inv.totalAmount - inv.paidAmount)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} (Vade: ${inv.dueDate || 'Belirtilmemiş'})`}
                placeholder="Fatura seçin..."
                className="rounded-xl border-slate-200"
              />
            </div>

            {offsetTargetInvoice && (
              <div>
                <label className="text-xs font-black text-slate-600 block mb-2">Mahsup Tutarı (₺)</label>
                <InputNumber
                  value={offsetAmount}
                  onValueChange={(e) => setOffsetAmount(e.value || 0)}
                  mode="decimal"
                  min={0.01}
                  max={Math.min(
                    offsetMode === 'sales' ? (offsetSourceInvoice.totalAmount - offsetSourceInvoice.paidAmount) : offsetSourceInvoice.remainingAmount,
                    offsetMode === 'sales' ? offsetTargetInvoice.remainingAmount : (offsetTargetInvoice.totalAmount - offsetTargetInvoice.paidAmount)
                  )}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  className="rounded-xl"
                  inputClassName="font-black"
                />
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        header="Fatura Seçimi ile Mahsuplaşma"
        visible={showGeneralOffsetDialog}
        style={{ width: '850px' }}
        modal
        onHide={() => setShowGeneralOffsetDialog(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button
              label="Vazgeç"
              icon="pi pi-times"
              className="p-button-text p-button-secondary !rounded-xl font-bold"
              onClick={() => setShowGeneralOffsetDialog(false)}
            />
            <Button
              label="Mahsup Et"
              icon="pi pi-sync"
              loading={offsetSubmitting}
              disabled={offsetSteps.length === 0}
              className="p-button-primary !rounded-xl font-bold !bg-purple-600 !border-purple-600 shadow-md shadow-purple-500/20"
              onClick={handleGeneralOffsetSubmit}
            />
          </div>
        )}
      >
        <div className="flex flex-col gap-4 p-fluid">
          <div className="p-3 bg-purple-50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/30 rounded-xl text-purple-900 dark:text-purple-300 text-xs font-bold leading-relaxed">
            Lütfen mahsup etmek istediğiniz müşteri (satış) faturalarını ve tedarikçi (alış) faturalarını tablolardan seçin. Sistem, seçtiğiniz faturaların bakiyelerini otomatik olarak eşleştirecektir.
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SOL - Müşteri Satış Faturaları */}
            <div className="border border-slate-100 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-950 shadow-sm">
              <span className="text-xs font-black text-slate-700 dark:text-slate-300 block mb-2 uppercase tracking-wide">Müşteri Faturaları (Satış)</span>
              <DataTable
                value={customerInvoices.filter(inv => inv.totalAmount - inv.paidAmount > 0.01)}
                selection={selectedOffsetCustomerInvoices}
                onSelectionChange={(e) => setSelectedOffsetCustomerInvoices(e.value || [])}
                dataKey="id"
                responsiveLayout="scroll"
                className="p-datatable-sm text-[11px]"
                emptyMessage="Açık satış faturası bulunamadı."
                scrollable
                scrollHeight="200px"
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3em' }}></Column>
                <Column field="saleNo" header="Fatura No" className="font-bold text-slate-800 dark:text-slate-200"></Column>
                <Column field="dueDate" header="Vade" className="text-slate-500" body={(r) => r.dueDate || '-'}></Column>
                <Column header="Kalan Bakiye" className="font-semibold text-right" body={(r) => `₺${(r.totalAmount - r.paidAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}></Column>
              </DataTable>
            </div>

            {/* SAĞ - Tedarikçi Alış Faturaları */}
            <div className="border border-slate-100 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-950 shadow-sm">
              <span className="text-xs font-black text-slate-700 dark:text-slate-300 block mb-2 uppercase tracking-wide">Tedarikçi Faturaları (Alış)</span>
              <DataTable
                value={invoiceHistory.filter(inv => inv.isDueTrackingActive && inv.remainingAmount > 0.01)}
                selection={selectedOffsetSupplierInvoices}
                onSelectionChange={(e) => setSelectedOffsetSupplierInvoices(e.value || [])}
                dataKey="id"
                responsiveLayout="scroll"
                className="p-datatable-sm text-[11px]"
                emptyMessage="Açık alış faturası bulunamadı."
                scrollable
                scrollHeight="200px"
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3em' }}></Column>
                <Column field="invoiceNo" header="Fatura No" className="font-bold text-slate-800 dark:text-slate-200"></Column>
                <Column field="dueDate" header="Vade" className="text-slate-500" body={(r) => r.dueDate || '-'}></Column>
                <Column header="Kalan Bakiye" className="font-semibold text-right" body={(r) => `₺${r.remainingAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}></Column>
              </DataTable>
            </div>
          </div>

          {offsetSteps.length > 0 && (
            <div className="mt-2">
              <label className="text-xs font-black text-slate-600 dark:text-slate-400 block mb-2">Mahsup Eşleştirme Önizlemesi</label>
              <div className="border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-900/40">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-bold border-b border-slate-100 dark:border-slate-800">
                      <th className="p-2.5">Müşteri Faturası (Satış)</th>
                      <th className="p-2.5">Tedarikçi Faturası (Alış)</th>
                      <th className="p-2.5 text-right">Mahsup Tutarı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offsetSteps.map((step, idx) => (
                      <tr key={idx} className="border-b last:border-0 border-slate-100 dark:border-slate-800/50 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors">
                        <td className="p-2.5 font-bold text-slate-700 dark:text-slate-300">{step.customerInvoice.saleNo}</td>
                        <td className="p-2.5 font-semibold text-slate-500 dark:text-slate-400">{step.supplierInvoice.invoiceNo}</td>
                        <td className="p-2.5 text-right font-black text-purple-600 dark:text-purple-400">
                          ₺{step.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex justify-between items-center p-3 bg-purple-100/50 dark:bg-purple-950/20 border border-purple-200/30 rounded-xl">
                <span className="text-xs font-bold text-purple-900 dark:text-purple-300">Toplam Mahsup Tutarı</span>
                <span className="text-sm font-black text-purple-700 dark:text-purple-400">
                  ₺{totalOffsetAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Ekstre İndirme Seçenekleri Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 dark:bg-emerald-950/50 p-2 rounded-lg">
              <i className="pi pi-file-pdf text-emerald-600 dark:text-emerald-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 m-0">Ekstre Seçenekleri</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium m-0">Tarih aralığı ve banka hesaplarını belirleyerek PDF dökümü oluşturun</p>
            </div>
          </div>
        }
        visible={showStatementDialog}
        style={{ width: '460px' }}
        onHide={() => setShowStatementDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
      >
        <div className="flex flex-col gap-4 pt-3">
          <div className="bg-emerald-50 dark:bg-emerald-950/20 p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
            <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 m-0">
              A4 formatında PDF ekstre dökümü seçenekleri.
            </p>
          </div>
          
          {/* 1) Tarih Seçimi */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Başlangıç Tarihi</label>
              <Calendar 
                value={statementStartDate} 
                onChange={(e) => setStatementStartDate(e.value)} 
                dateFormat="dd.mm.yy" 
                showIcon 
                placeholder="Seçiniz"
                className="p-inputtext-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Bitiş Tarihi</label>
              <Calendar 
                value={statementEndDate} 
                onChange={(e) => setStatementEndDate(e.value)} 
                dateFormat="dd.mm.yy" 
                showIcon 
                placeholder="Seçiniz"
                className="p-inputtext-sm"
              />
            </div>
          </div>

          {/* 2) Seçenekler */}
          <div className="flex flex-col gap-2 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <Checkbox 
                inputId="showProductsOpt" 
                checked={statementShowProducts} 
                onChange={(e) => setStatementShowProducts(e.checked)} 
              />
              <label htmlFor="showProductsOpt" className="text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                Alış faturasına ait ürünleri göster (Detaylı)
              </label>
            </div>
          </div>

          {supplier?.relatedCustomerId && supplier?.relatedCustomerId > 0 && (
            <div className="flex flex-col gap-2 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-800 mt-1">
              <div className="flex items-center gap-2">
                <Checkbox 
                  inputId="includeSalesOpt" 
                  checked={statementIncludeSales} 
                  onChange={(e) => setStatementIncludeSales(e.checked)} 
                />
                <label htmlFor="includeSalesOpt" className="text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                  Satış Faturalarını Dahil Et (Konsolide Ekstre)
                </label>
              </div>
            </div>
          )}

          {/* 3) Banka Hesapları */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400">Döküme Eklenecek Banka Hesapları</label>
            <div className="max-h-[120px] overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-xl p-2 flex flex-col gap-2 bg-white dark:bg-slate-950">
              {financialAccounts.filter(a => a.accountType === 2).length === 0 ? (
                <span className="text-xs text-slate-400 italic p-1">Kayıtlı banka hesabı bulunamadı.</span>
              ) : (
                financialAccounts.filter(a => a.accountType === 2).map((acc) => (
                  <div key={acc.id} className="flex items-center gap-2">
                    <Checkbox 
                      inputId={`bank_acc_${acc.id}`} 
                      value={acc}
                      checked={statementSelectedBanks.some(b => b.id === acc.id)}
                      onChange={(e) => {
                        if (e.checked) {
                          setStatementSelectedBanks([...statementSelectedBanks, acc]);
                        } else {
                          setStatementSelectedBanks(statementSelectedBanks.filter(b => b.id !== acc.id));
                        }
                      }}
                    />
                    <label htmlFor={`bank_acc_${acc.id}`} className="text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                      <strong>{acc.name}</strong> {acc.accountNumber ? `(${acc.accountNumber})` : ''}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Download Button */}
          <Button
            label="PDF Dökümü Oluştur (A4 PDF)"
            icon="pi pi-file-pdf"
            className="!bg-emerald-600 border-none font-black !py-3 !rounded-xl text-white font-bold mt-2"
            onClick={async () => {
              setShowStatementDialog(false);
              try {
                const requestBody = {
                  startDate: statementStartDate ? statementStartDate.toISOString() : null,
                  endDate: statementEndDate ? statementEndDate.toISOString() : null,
                  includeSales: statementIncludeSales && !!supplier?.relatedCustomerId,
                  showProducts: statementShowProducts
                };
                const res = await SupplierService.getSupplierStatement(id, requestBody);
                if (res.data && res.data.success) {
                  supplierStatementReportRef.current?.downloadPDF(
                    res.data.data, 
                    statementShowProducts, 
                    statementStartDate, 
                    statementEndDate, 
                    statementSelectedBanks,
                    requestBody.includeSales
                  );
                } else {
                  toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data?.message || 'Ekstre alınamadı', life: 3000 });
                }
              } catch (err) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ekstre indirilirken hata oluştu', life: 3000 });
              }
            }}
          />

          <div className="flex justify-end mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <Button
              label="Kapat"
              icon="pi pi-times"
              className="p-button-text p-button-secondary font-bold max-w-[100px]"
              onClick={() => setShowStatementDialog(false)}
            />
          </div>
        </div>
      </Dialog>

      <SupplierStatementReport ref={supplierStatementReportRef} />
    </div>
  );
}
